import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  IoCloudUploadOutline,
  IoTrashOutline,
  IoAttach,
  IoPrintOutline,
} from "react-icons/io5";
import Combobox from "../../../ui/combobox";
import MultiSelect from "../../../ui/multiselect";
import { fetchWorkOrdersWithDetails } from "../../../pages/Dashboard/Invoice/module";
import SearchableDropdown from "../../../ui/searchabledropdown";
import {
  checkIfValuesExist,
  getDetailsFromFormFields,
  getDropdownOptions,
  getValueWithType,
  showMessageBox,
  objectToQueryString,
  toStartOfDay,
  toEndOfDay,
  formatDateForDisplay,
} from "../../fn";
import "./form.css";
import {
  transactionSectionLoads,
  transactionSummaryFields,
  calculateTransactionTotals,
  getSummaryColSpan,
} from "../../../pages/Dashboard/Invoice/module";
import { hasPermission } from "../../../../utils/permission";
import Modal from "../../../ui/modal";
import { formatDateTime } from "../../../../utils/fn";


// ✅ NEW: Helper to check and refresh token
const checkAndRefreshToken = async () => {
  const token = localStorage.getItem("zTaskAuthToken");
  if (!token) {
    console.error('❌ No auth token found');
    window.location.href = '/login'; // Redirect to login
    return false;
  }
  
  // Optional: Add token validation/refresh logic here
  // For now, just check if it exists
  return true;
};

// --- Utility: Get file name from URL ---
const getFileNameFromUrl = (url) => {
  try {
    return decodeURIComponent(url.split("/").pop());
  } catch {
    return url;
  }
};
// --- Utility: Fetch all users for mapping ---
const TOOLS_API_URL = process.env.REACT_APP_NODE_SERVICE_BASE_URL + "/api";
const ASSET_API_URL = process.env.REACT_APP_ASSET_SERVICE_BASE_URL;
const getApiHeaders = (isMultipart = false) => {
  const token = localStorage.getItem("zTaskAuthToken");
  if (!token) throw new Error("Authentication token not found.");
  const headers = { Authorization: `Bearer ${token}` };
  if (!isMultipart) {
    headers["Content-Type"] = "application/json";
    headers["Accept"] = "application/json";
  }
  return headers;
};
const fetchAllUsersForMappingAPI = async () => {
  const response = await fetch(`${TOOLS_API_URL}/get-column/users/name`, {
    headers: getApiHeaders(),
  });
  if (!response.ok)
    throw new Error(`Failed to fetch users list: ${response.statusText}`);
  const data = await response.json();
  if (Array.isArray(data)) {
    return data.map((user) => ({ id: user.id, name: user.value }));
  }
  return [];
};
// --- Utility: Delete attachment ---
const ATTACHMENT_API_URL = process.env.REACT_APP_NODE_SERVICE_BASE_URL;
const deleteAttachmentAPI = async (attachmentId) => {
  const response = await fetch(
    `${ATTACHMENT_API_URL}/attachment/api/attachments/${attachmentId}`,
    {
      method: "DELETE",
      headers: getApiHeaders(),
    }
  );
  if (!response.ok && response.status !== 204) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || "Failed to delete attachment");
  }
  return true;
};
// --- Upload Attachments API ---
const uploadAttachments = async (
  masterId,
  moduleName,
  files,
  descriptions
) => {
  if (!masterId || !moduleName || files.length === 0) {
    console.log('❌ [Upload] Invalid parameters:', { masterId, moduleName, filesCount: files.length });
    return;
  }
  
  const token = localStorage.getItem("zTaskAuthToken");
  
  if (!token) {
    throw new Error("Authentication token not found");
  }
  
  console.log('📤 [Upload] Starting upload:', {
    masterId,
    moduleName,
    filesCount: files.length,
    descriptionsCount: descriptions.length
  });
  
  const uploadFormData = new FormData();
  
  // ✅ CRITICAL: Append files with correct field name
  files.forEach((file, index) => {
    uploadFormData.append("files", file);
    console.log(`📎 [Upload] Added file ${index + 1}:`, file.name, 'Size:', file.size);
  });
  
  // ✅ CRITICAL: Send descriptions as comma-separated string (not JSON)
  const descriptionsString = descriptions.map(desc => desc || "").join(",");
  uploadFormData.append("descriptions", descriptionsString);
  
  console.log('📤 [Upload] FormData prepared:', {
    files: files.length,
    descriptions: descriptionsString
  });
  
  try {
    const response = await fetch(
      `${ATTACHMENT_API_URL}/attachment/api/attachments/${masterId}/${moduleName}`,
      {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          // ❌ DO NOT set Content-Type - browser will set it with boundary
        },
        body: uploadFormData,
      }
    );
    
    console.log('📥 [Upload] Response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ [Upload] Error response:', errorText);
      
      let errorMessage;
      try {
        const err = JSON.parse(errorText);
        errorMessage = err.message || err.error || "Failed to upload attachments";
      } catch {
        errorMessage = errorText || "Failed to upload attachments";
      }
      
      throw new Error(errorMessage);
    }
    
    const result = await response.json();
    console.log('✅ [Upload] Success:', result);
    
    return result;
  } catch (error) {
    console.error('❌ [Upload] Exception:', error);
    throw error;
  }
};
// --- NEW: Helper function for "Invoice For" validation ---
const validateInvoiceForUniqueness = (
  currentTran,
  allTransactions,
  currentIndex,
  transactionFormConfig
) => {
  const errors = {};
  const identifierField = transactionFormConfig.formFields.find(
    (f) => f.identifier
  )?.fieldName;
  const invoiceForField = "invoice_for";
  const uniqueInvoiceForValues = ["mob", "demob"];
  if (
    !identifierField ||
    !currentTran[invoiceForField] ||
    !uniqueInvoiceForValues.includes(
      String(currentTran[invoiceForField]).toLowerCase()
    )
  ) {
    return errors;
  }
  const currentAssetId = currentTran[identifierField]?.id;
  if (!currentAssetId) {
    return errors;
  }
  const isDuplicate = allTransactions.some((otherTran, otherIndex) => {
    if (currentIndex === otherIndex) return false;
    const otherAssetId = otherTran[identifierField]?.id;
    return (
      otherAssetId === currentAssetId &&
      String(otherTran[invoiceForField]).toLowerCase() ===
        String(currentTran[invoiceForField]).toLowerCase()
    );
  });
  if (isDuplicate) {
    errors[
      invoiceForField
    ] = `This option can only be selected once for this asset.`;
  }
  return errors;
};
// 🔥 NEW: Helper function to format dates in the list table
const formatDateForTable = (dateValue) => {
  if (!dateValue) return "N/A";
  const date = new Date(dateValue);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};
const StatusView = ({ currentStatus, masterFormData, statusField }) => {
  if (!currentStatus) return null;
  
  const currentStatusValue = masterFormData[statusField];
  const isRejected = currentStatusValue?.includes("Rejected");
  
  return (
    <div className={`status-view ${isRejected ? 'status-rejected' : ''}`}>
      <h4>Current Status: {masterFormData[statusField]}</h4>
      
      {/* ✅ Show rejection details if rejected */}
      {isRejected && (
        <div className="rejection-info">
          {currentStatusValue === "Rejected by Checker" && masterFormData.inv_check_remark && (
            <div className="rejection-details">
              <strong>Checker Rejection Reason:</strong>
              <p>{masterFormData.inv_check_remark}</p>
            </div>
          )}
          {currentStatusValue === "Rejected by Approver" && masterFormData.inv_approval_remark && (
            <div className="rejection-details">
              <strong>Approver Rejection Reason:</strong>
              <p>{masterFormData.inv_approval_remark}</p>
            </div>
          )}
          <p className="rejection-action-needed">
            ⚠️ Please review the rejection reason, make necessary changes, and resubmit for checking.
          </p>
        </div>
      )}
      
      {currentStatus.fields && currentStatus.fields.length > 0 && (
        <div className="status-details">
          {currentStatus.fields.map((field) => (
            <p key={field}>
              {field}: {masterFormData[field] || "N/A"}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};
const Form = ({
  masterFormConfig,
  transactionFormConfig,
  initialData = {},
  onFormSuccess,
  moduleName,
  submitTogether = false,
  parentIdColumnName,
  masterCalculatedFieldsFn,
  statusField,
  disabledOnStatusValues,
  statuses,
}) => {
  const navigate = useNavigate();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [masterFormData, setMasterFormData] = useState({});
  const [masterFormErrors, setMasterFormErrors] = useState({});
  const [isLoadingMaster, setIsLoadingMaster] = useState(false);
  const [masterDropdownOptions, setMasterDropdownOptions] = useState({});
  const [masterListData, setMasterListData] = useState({});
  const [fileDescriptions, setFileDescriptions] = useState([]);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [transactionErrors, setTransactionErrors] = useState({});
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [transactionDropdownOptions, setTransactionDropdownOptions] = useState(
    {}
  );
  const [transactionComboboxOptions, setTransactionComboboxOptions] = useState(
    {}
  );
  const [masterRecordId, setMasterRecordId] = useState(null);
  const [masterRecordDates, setMasterRecordDates] = useState({});
  const [isFormLoading, setIsFormLoading] = useState(true);
  const [approvePermit, setApprovePermit] = useState(false);
  const [checkPermit, setCheckPermit] = useState(false);
  const [printPermit, setPrintPermit] = useState(false);
  const [approvers, setApprovers] = useState([]);
  const [checkers, setCheckers] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [workOrderCount, setWorkOrderCount] = useState(0);
  const currentStatus = useMemo(() =>
    statuses?.find((stat) =>
      stat.value.includes(masterFormData[statusField])
    ), [statuses, masterFormData[statusField]]);
  // Function to check if checker selection should be shown
 const shouldShowCheckerSelection = useCallback(() => {
  const currentStatusValue = masterFormData[statusField];
  // ✅ Show for Draft, Rejected by Checker, AND Rejected by Approver
  return currentStatusValue === "Drafted" || 
         currentStatusValue === "Rejected by Checker" ||
         currentStatusValue === "Rejected by Approver";
}, [masterFormData, statusField]);

const shouldShowApproverSelection = useCallback(() => {
  const currentStatusValue = masterFormData[statusField];
  
  console.log('🔍 [shouldShowApproverSelection] Current status:', currentStatusValue);
  console.log('🔍 [shouldShowApproverSelection] checkPermit:', checkPermit);
  console.log('🔍 [shouldShowApproverSelection] approvePermit:', approvePermit);
  console.log('🔍 [shouldShowApproverSelection] masterRecordId:', masterRecordId);
  
  // ✅ Show for CHECKER when status is "Waiting for check" (to select approvers)
  if (masterRecordId && currentStatusValue === "Waiting for check" && checkPermit && !approvePermit) {
    return true;
  }
  
  return false;
}, [masterFormData, statusField, checkPermit, approvePermit, masterRecordId]);

// NEW: Function to check if approver actions should be shown
const shouldShowApproverActions = useCallback(() => {
  const currentStatusValue = masterFormData[statusField];
  
  console.log('🔍 [shouldShowApproverActions] Current status:', currentStatusValue);
  console.log('🔍 [shouldShowApproverActions] approvePermit:', approvePermit);
  console.log('🔍 [shouldShowApproverActions] masterRecordId:', masterRecordId);
  
  // ✅ Show for APPROVER when status is "Waiting for approval"
  if (masterRecordId && currentStatusValue === "Waiting for approval" && approvePermit) {
    return true;
  }
  
  return false;
}, [masterFormData, statusField, approvePermit, masterRecordId]);


// 🔍 DEBUG: Monitor state changes
useEffect(() => {
  console.log('🔍 [Form State] Current Status:', masterFormData[statusField]);
  console.log('🔍 [Form State] Master Record ID:', masterRecordId);
  console.log('🔍 [Form State] Check Permit:', checkPermit);
  console.log('🔍 [Form State] Approve Permit:', approvePermit);
  console.log('🔍 [Form State] Approvers:', approvers);
  console.log('🔍 [Form State] Should Show Approver Selection:', shouldShowApproverSelection());
}, [masterFormData[statusField], masterRecordId, checkPermit, approvePermit, approvers, shouldShowApproverSelection]);

  // NEW: Check if print should be enabled (status is Approved / Sent)
  const isPrintEnabled = useCallback(() => {
    const currentStatusValue = masterFormData[statusField];
    const statusOk =
      currentStatusValue === "Approved" ||
      currentStatusValue === "Sent to customer";
    const canPrint =
      printPermit ||
      !!hasPermission("invoice", "VIEW") ||
      !!hasPermission("VIEW INVOICE");
    return statusOk && canPrint;
  }, [masterFormData, statusField, printPermit]);
  const userMap = useMemo(
    () =>
      (users || []).reduce((acc, user) => {
        acc[user.id] = user.name;
        return acc;
      }, {}),
    [users]
  );
  // Fetch users for mapping
  useEffect(() => {
    fetchAllUsersForMappingAPI()
      .then((mappedUsers) => setUsers(mappedUsers))
      .catch((err) => {
        console.error(
          "Failed to fetch user list. 'Added by' column may show IDs.",
          err
        );
        setUsers([]);
      });
  }, []);
  // Check print permission - runs on component mount and when status changes
  useEffect(() => {
    try {
      setPrintPermit(!!hasPermission("invoice", "PRINT"));
    } catch (error) {
      console.error("Error checking print permission:", error);
      setPrintPermit(false);
    }
  }, [masterFormData[statusField]]);

  // Check check permission
  useEffect(() => {
    try {
      setCheckPermit(!!hasPermission("invoice", "CHECK"));
    } catch (error) {
      console.error("Error checking check permission:", error);
      setCheckPermit(false);
    }
  }, [masterFormData[statusField]]);

  // --- File Handlers ---
  const addFilesAndDescriptions = (newFiles) => {
    setSelectedFiles((prev) => [...prev, ...newFiles]);
    setFileDescriptions((prev) => [
      ...prev,
      ...Array(newFiles.length).fill(""),
    ]);
  };
  const handleFileChange = (e) => {
    if (e.target.files) {
      addFilesAndDescriptions(Array.from(e.target.files));
    }
    e.target.value = null;
  };
  const handleRemoveNewFile = (indexToRemove) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== indexToRemove));
    setFileDescriptions((prev) => prev.filter((_, i) => i !== indexToRemove));
  };
  const handleDescriptionChange = (index, value) => {
    const newDescriptions = [...fileDescriptions];
    newDescriptions[index] = value;
    setFileDescriptions(newDescriptions);
  };
  const handleDeleteExistingAttachment = async (attachmentId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this attachment?");
    if (!confirmDelete) return;
    try {
      await deleteAttachmentAPI(attachmentId);
      setExistingAttachments((prev) => prev.filter((att) => att.id !== attachmentId));
      showMessageBox("Attachment deleted successfully.", "success");
    } catch (error) {
      console.error("Error deleting attachment:", error);
      showMessageBox(`Failed to delete attachment: ${error.message}`, "error");
    }
  };
  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) {
      addFilesAndDescriptions(Array.from(e.dataTransfer.files));
      e.dataTransfer.clearData();
    }
  }, []);
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items?.length) setIsDragging(true);
  }, []);
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);
  useEffect(() => {
    if (masterRecordId && currentStatus?.permissionName) {
      const res = !!hasPermission("invoice", currentStatus.permissionName);
      if (currentStatus.permissionName === "CHECK") {
        setCheckPermit(res);
      } else if (currentStatus.permissionName === "APPROVE") {
        setApprovePermit(res);
      }
    }
  }, [currentStatus, masterRecordId]);
  const {
    fields: masterRenderFields,
    defaultValues: masterDefaultFieldValues,
  } = useMemo(
    () =>
      getDetailsFromFormFields(
        masterFormConfig.formFields,
        masterFormData,
        transactions
      ),
    [masterFormConfig.formFields, masterFormData, transactions]
  );
// ✅ FIXED: Reset ALL state when invoice ID changes AND force re-render
useEffect(() => {
  console.log('🔄🔄🔄 [RESET useEffect] TRIGGERED! 🔄🔄🔄');
  console.log('📋 [RESET] initialData:', initialData);
  console.log('📋 [RESET] initialData.id:', initialData.id);
  console.log('📋 [RESET] Current masterRecordId:', masterRecordId);
  console.log('📋 [RESET] Current isEditMode:', isEditMode);
  
  // ✅ CRITICAL: Detect if this is a real change
  const isActualChange = initialData.id !== masterRecordId;
  
  console.log('🔍 [RESET] Is this an actual change?', isActualChange);
  
  if (!isActualChange && masterRecordId !== null) {
    console.log('⏭️ [RESET] No change detected, skipping reset');
    return;
  }
  
  // Clear ALL form state when switching invoices
  console.log('🧹 [RESET] Clearing all state...');
  setMasterFormData({});
  setTransactions([]);
  setMasterFormErrors({});
  setTransactionErrors({});
  setMasterDropdownOptions({});
  setMasterListData({});
  setTransactionDropdownOptions({});
  setTransactionComboboxOptions({});
  setExistingAttachments([]);
  setSelectedFiles([]);
  setFileDescriptions([]);
  setCheckers([]);
  setApprovers([]);
  setMasterRecordDates({});
  setWorkOrderCount(0);
  
  // Set loading state and edit mode
  if (initialData.id) {
    console.log('📝 [RESET] Switching to EDIT mode for invoice:', initialData.id);
    setIsFormLoading(true);
    setIsEditMode(true);
    setMasterRecordId(initialData.id);
  } else {
    console.log('✨ [RESET] Switching to CREATE mode');
    setIsFormLoading(false);
    setIsEditMode(false);
    setMasterRecordId(null);
  }
  
  console.log('🏁 [RESET] Reset useEffect complete!');
// }, [initialData.id]); // ✅ CRITICAL: Only depend on initialData.id, NOT the whole initialData object
}, [initialData.id, masterFormConfig.listApi]);

useEffect(() => {
  console.log('🚀🚀🚀 [LOAD useEffect] TRIGGERED! 🚀🚀🚀');
  console.log('📊 [LOAD] Current State:');
  console.log('  - initialData.id:', initialData.id);
  console.log('  - masterRecordId:', masterRecordId);
  console.log('  - isEditMode:', isEditMode);
  console.log('  - isFormLoading:', isFormLoading);
  
  const loadInitialDataAndDependencies = async () => {
    // ✅ FIX 1: Only show loading for EDIT mode (when editing existing invoice)
    const isCurrentlyEditMode = Boolean(initialData.id);
    
    console.log('🔍 [Load] Starting to load form data...');
    console.log('🔍 [Load] Edit Mode:', isCurrentlyEditMode);
    console.log('🔍 [Load] Initial data ID:', initialData.id);
    console.log('🔍 [Load] Master Record ID:', masterRecordId);
    
    // ✅ CRITICAL: Only set loading state for edit mode
    if (isCurrentlyEditMode) {
      setIsFormLoading(true);
      setIsEditMode(true);
    } else {
      // For new invoices, immediately show the form
      setIsFormLoading(false);
      setIsEditMode(false);
    }

    // ✅ Check token first
    const hasValidToken = await checkAndRefreshToken();
    if (!hasValidToken) {
      setIsFormLoading(false);
      return;
    }
    
    const token = localStorage.getItem("zTaskAuthToken");
    console.log('🔑 [Load] Token exists:', !!token);
    console.log('🔑 [Load] Token preview:', token ? token.substring(0, 20) + '...' : 'NONE');
    
    const { defaultValues: initialDefaultValues } = getDetailsFromFormFields(
      masterFormConfig.formFields,
      {},
      []
    );
    
    let loadedMasterData = { ...initialDefaultValues };
    let formattedTransactions = [];
    const masterId = initialData.id || null;

    try {
      // ========================================
      // 🔥 ONLY FETCH DATA IF EDITING EXISTING INVOICE
      // ========================================
      if (masterId) {
        console.log('📡 [Load] Fetching invoice data for ID:', masterId);
        console.log('📡 [Load] API URL will be:', `${masterFormConfig.listApi}/${masterId}`);
        
        setMasterRecordId(masterId);

        // ========================================
        // STEP 1: Fetch Master Data
        // ========================================
        console.log('📡 [Load] Step 1: Fetching master data...');
        console.log('📡 [Load] Calling fetch for master data...');
        const masterResponse = await fetch(
          `${masterFormConfig.listApi}/${masterId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        
        if (!masterResponse.ok) {
          throw new Error("Failed to fetch master data.");
        }
        
        const fetchedMasterData = await masterResponse.json();
        loadedMasterData = { ...loadedMasterData, ...fetchedMasterData };
        
        console.log('✅ [Load] Master data fetched');

       // 🔥 NEW: Pre-populate work orders cache if editing existing invoice
if (fetchedMasterData.wo_id) {
  console.log('🔄 [Load] Pre-populating work orders cache for editing...');
  try {
    await fetchWorkOrdersWithDetails(true); // Force refresh cache
    console.log('✅ [Load] Work orders cache populated');
  } catch (error) {
    console.error('⚠️ [Load] Failed to populate work orders cache:', error);
  }
}

        // Format Master Data (dates, multiselect, etc.)
        for (const field of masterFormConfig.formFields) {
          if (field.inputType === "date" && loadedMasterData[field.fieldName]) {
            loadedMasterData[field.fieldName] = loadedMasterData[field.fieldName].split("T")[0];
          } else if (
            field.isMulti &&
            loadedMasterData[field.fieldName] &&
            typeof loadedMasterData[field.fieldName] === "string"
          ) {
            try {
              loadedMasterData[field.fieldName] = JSON.parse(
                loadedMasterData[field.fieldName].replace("{", "[").replace("}", "]")
              );
            } catch (e) {
              console.error("Failed to parse multi-select string:", e);
              loadedMasterData[field.fieldName] = [];
            }
          }
        }

        // ========================================
        // STEP 2: Fetch Existing Attachments
        // ========================================
        console.log('📡 [Load] Step 2: Fetching attachments...');
        try {
          const attRes = await fetch(
            `${ATTACHMENT_API_URL}/attachment/api/attachments/${masterId}/${moduleName}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (attRes.ok) {
            const attachments = await attRes.json();
            setExistingAttachments(attachments.filter((att) => att.active));
            console.log('✅ [Load] Attachments fetched');
          }
        } catch (error) {
          console.error("Error fetching attachments:", error);
        }

        // ========================================
        // STEP 3: Fetch Transaction Data
        // ========================================
        console.log('📡 [Load] Step 3: Fetching transactions...');
        
        let tranApiUrl;
        if (transactionFormConfig.listApi) {
          tranApiUrl = `${transactionFormConfig.listApi}/${masterId}`;
        } else {
          tranApiUrl = `${transactionFormConfig.submitApi}/${masterId}`;
        }
        
        console.log('📡 [Load] Transaction API URL:', tranApiUrl);

        try {
          const tranResponse = await fetch(tranApiUrl, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
          });

          if (!tranResponse.ok) {
            throw new Error(`Failed to fetch transactions: ${tranResponse.status}`);
          }

          const tranResponseText = await tranResponse.text();
          let loadedTransactions = JSON.parse(tranResponseText);

          if (!Array.isArray(loadedTransactions)) {
            throw new Error('Transaction API returned non-array response');
          }

          console.log('✅ [Load] Transactions fetched:', loadedTransactions.length);

          // ========================================
          // STEP 4: Format Transaction Data
          // ========================================
          console.log('🔄 [Load] Step 4: Formatting transactions...');
          
          const formattedTransactionsPromises = loadedTransactions.map(
            async (tran, index) => {
              const values = getDetailsFromFormFields(
                transactionFormConfig.formFields,
                loadedMasterData,
                []
              );

              let newTran = {
                ...values,
                ...values.defaultValues,
                ...tran,
                id: tran.id || Date.now() + index,
                dbId: tran.id,
              };

              if (!newTran.inv_sr_no) {
                newTran.inv_sr_no = index + 1;
              }

              const promises = [];

              for (const field of transactionFormConfig.formFields) {
                if (!newTran.hasOwnProperty(field.fieldName)) {
                  continue;
                }

                if (
                  (field.inputType === "datetime-local" || field.inputType === "date") &&
                  newTran[field.fieldName]
                ) {
                  newTran[field.fieldName] = newTran[field.fieldName].split("T")[0];
                } else if (
                  field.inputType === "number" &&
                  newTran[field.fieldName] !== null &&
                  newTran[field.fieldName] !== undefined
                ) {
                  newTran[field.fieldName] = parseFloat(newTran[field.fieldName]);
                }

                if (field.load) {
                  promises.push(
                    field.load(newTran).catch(error => {
                      console.error(`Error in load function for ${field.fieldName}:`, error);
                      return {};
                    })
                  );
                }
              }

              try {
                const loadedFieldResults = await Promise.all(promises);
                for (const result of loadedFieldResults) {
                  if (result) {
                    Object.assign(newTran, result);
                  }
                }
              } catch (error) {
                console.error(`Error loading field data for transaction ${index + 1}:`, error);
              }

              return newTran;
            }
          );

          formattedTransactions = await Promise.all(formattedTransactionsPromises);
          console.log('✅ [Load] All transactions formatted');

          // ========================================
          // STEP 5: Re-calculate transaction values
          // ========================================
          console.log('🔄 [Load] Step 5: Re-calculating values...');
          
          const recalculatedTransactions = await Promise.all(
            formattedTransactions.map(async (tran) => {
              let tempTran = { ...tran };
              for (const rule of transactionFormConfig.calculationRules) {
                try {
                  const ruleResult = await rule.calculate(
                    tempTran,
                    null,
                    loadedMasterData,
                    formattedTransactions
                  );
                  Object.assign(tempTran, ruleResult);
                } catch (calcError) {
                  console.error('Calculation error:', calcError);
                }
              }
              return tempTran;
            })
          );

          formattedTransactions = recalculatedTransactions;

          // ========================================
          // STEP 6: Fetch asset data for transactions
          // ========================================
          console.log('🔄 [Load] Step 6: Fetching asset data...');
          
          const transactionIdentifierField = transactionFormConfig.formFields.find(
            (f) => f.identifier
          )?.fieldName;

          if (transactionIdentifierField) {
            const enrichedTransactions = await Promise.all(
              formattedTransactions.map(async (tran, index) => {
                let enrichedTran = { ...tran };
                
                if (enrichedTran[transactionIdentifierField]) {
                  try {
                    const assetId = typeof enrichedTran[transactionIdentifierField] === 'object'
                      ? enrichedTran[transactionIdentifierField].id || enrichedTran[transactionIdentifierField]
                      : enrichedTran[transactionIdentifierField];

                    const assetResponse = await fetch(
                      `${ASSET_API_URL}/api/assets/${assetId}`,
                      {
                        headers: {
                          Authorization: `Bearer ${token}`,
                          'Content-Type': 'application/json',
                          'Accept': 'application/json'
                        }
                      }
                    );

                    if (assetResponse.ok) {
                      const assetData = await assetResponse.json();

                      const labelParts = [assetData.name];
                      if (assetData.registration_number) labelParts.push(assetData.registration_number);
                      if (assetData.serial_number) labelParts.push(assetData.serial_number);
                      if (assetData.asset_type) labelParts.push(`(${assetData.asset_type})`);

                      enrichedTran[transactionIdentifierField] = {
                        id: assetData.id,
                        label: labelParts.join(' | '),
                        value: assetData.id,
                        name: assetData.name,
                        asset_type: assetData.asset_type,
                        registration_number: assetData.registration_number,
                        serial_number: assetData.serial_number,
                        original: assetData,
                      };
                    }
                  } catch (error) {
                    console.error(`Error fetching asset for transaction ${index + 1}:`, error);
                  }
                }
                
                return enrichedTran;
              })
            );

            formattedTransactions = enrichedTransactions;
            console.log('✅ [Load] All asset data loaded');
          }

          // Final re-calculation after asset enrichment
          for (let i = 0; i < formattedTransactions.length; i++) {
            let finalTran = { ...formattedTransactions[i] };
            for (const rule of transactionFormConfig.calculationRules) {
              try {
                const ruleResult = await rule.calculate(
                  finalTran,
                  null,
                  loadedMasterData,
                  formattedTransactions
                );
                Object.assign(finalTran, ruleResult);
              } catch (calcError) {
                console.error('Post-enrichment calc error:', calcError);
              }
            }
            formattedTransactions[i] = finalTran;
          }

          console.log('✅ [Load] Transaction loading complete');

        } catch (tranError) {
          console.error('Critical error loading transactions:', tranError);
          formattedTransactions = [];
          showMessageBox(`Failed to load invoice transactions: ${tranError.message}`, 'error');
        }
      }

      // ========================================
      // STEP 7: Fetch Initial Dropdowns & Lists
      // ========================================
      console.log('📡 [Load] Step 7: Fetching dropdowns and lists...');
      
      let currentDropdownOptions = {};
      let currentListData = {};
      let currentTransactionDropdowns = {};
      let currentTransactionComboboxes = {};

      for (const field of masterFormConfig.formFields) {
        if (field.tag === "list" && field.getRows) {
          const rows = await field.getRows(loadedMasterData);
          if (rows) currentListData[field.fieldName] = rows;
        } else if (field.getOptions) {
          const options = await field.getOptions(loadedMasterData);
          if (options) currentDropdownOptions[field.fieldName] = options;
        } else if (field.data && !field.data.dependsOnField) {
          const options = await getDropdownOptions(field, loadedMasterData);
          if (options) currentDropdownOptions[field.fieldName] = options;
        }
      }

      const woOptions = currentDropdownOptions.wo_id || [];
      setWorkOrderCount(woOptions.length);

      // ========================================
      // STEP 8: Run Initial Calculations
      // ========================================
      console.log('🔄 [Load] Step 8: Running initial calculations...');
      
      let calculatedValuesForInitialLoad = {};
      for (const rule of masterFormConfig.calculationRules) {
        const result = await rule.calculate({
          ...loadedMasterData,
          ...calculatedValuesForInitialLoad,
        });
        const [updatedFromCalc, dropdownsFromCalc, listsFromCalc] =
          result.length === 3 ? result : [result[0], result[1], {}];
        Object.assign(calculatedValuesForInitialLoad, updatedFromCalc);
        if (dropdownsFromCalc) Object.assign(currentDropdownOptions, dropdownsFromCalc);
        if (listsFromCalc) Object.assign(currentListData, listsFromCalc);
      }

      loadedMasterData = {
        ...loadedMasterData,
        ...calculatedValuesForInitialLoad,
      };

      // ========================================
      // STEP 9: Fetch options for transaction rows
      // ========================================
      if (formattedTransactions.length > 0) {
        console.log('📋 [Load] Step 9: Loading transaction dropdown options...');

        for (let index = 0; index < formattedTransactions.length; index++) {
          const tran = formattedTransactions[index];
          const singleRowDropdowns = {};
          const singleRowComboboxes = {};

          for (const field of transactionFormConfig.formFields) {
            if (field.tag === "select" || field.tag === "combobox") {
              if (!field.fieldName) continue;

              try {
                let options = [];

                if (field.getOptions) {
                  options = await field.getOptions(tran, loadedMasterData);
                } else if (field.data && (field.data.tableName || field.data.url)) {
                  options = await getDropdownOptions(field, tran, {});
                }

                if (field.tag === "select") {
                  singleRowDropdowns[field.fieldName] = options || [];
                } else if (field.tag === "combobox") {
                  singleRowComboboxes[field.fieldName] = options || [];
                }
              } catch (error) {
                console.error(`Error fetching options for ${field.fieldName} in row ${index}:`, error);
                if (field.tag === "select") {
                  singleRowDropdowns[field.fieldName] = [];
                } else {
                  singleRowComboboxes[field.fieldName] = [];
                }
              }
            }
          }

          currentTransactionDropdowns[index] = singleRowDropdowns;
          currentTransactionComboboxes[index] = singleRowComboboxes;
        }

        console.log('✅ [Load] All transaction options loaded');
      }

      // ========================================
      // STEP 10: Set final state
      // ========================================
      console.log('✅ [Load] Step 10: Setting final state...');
      
      setMasterDropdownOptions(currentDropdownOptions);
      setMasterListData(currentListData);
      setTransactionDropdownOptions(currentTransactionDropdowns);
      setTransactionComboboxOptions(currentTransactionComboboxes);
      setMasterFormData(loadedMasterData);
      setTransactions(formattedTransactions);
      setMasterRecordDates({
        start_date: loadedMasterData.start_date ? new Date(loadedMasterData.start_date) : null,
        end_date: loadedMasterData.end_date ? new Date(loadedMasterData.end_date) : null,
      });

      console.log('🎉 [Load] All data loaded successfully');
      
    } catch (error) {
      console.error("❌ Error loading form data:", error);
      showMessageBox(`Failed to load form details: ${error.message}`, "error");
    } finally {
      // ✅ CRITICAL: Always hide loading after data is loaded or error occurs
      if (isCurrentlyEditMode) {
        setIsFormLoading(false);
        console.log('✅ [Load] Loading complete - showing form');
      }
    }
  };

  loadInitialDataAndDependencies();
// }, [
//   initialData.id,
//   masterFormConfig.listApi,
//   transactionFormConfig.submitApi,
//   moduleName,
// ]);
}, [initialData.id, masterFormConfig.listApi]);


  const handleMasterChange = async (e, multiSelectValues = undefined) => {
    const { name, value, type, checked } = e?.target || {};
    let updatedData = { ...masterFormData };
    let fieldName = name;
    let fieldValue = value;
    if (multiSelectValues !== undefined) {
      fieldName = e;
      fieldValue = multiSelectValues;
    } else if (type === "checkbox") {
      fieldValue = checked;
    } else {
      fieldValue = value === "" ? null : value;
      if (type === "number") {
        fieldValue = value === "" ? null : parseFloat(value);
      }
    }
    updatedData[fieldName] = fieldValue;
    // 🔥 NEW: Real-time date validation
  if (fieldName === "start_date" || fieldName === "end_date") {
    const startDate = fieldName === "start_date" ? fieldValue : updatedData.start_date;
    const endDate = fieldName === "end_date" ? fieldValue : updatedData.end_date;
    
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      if (end < start) {
        // Show error immediately
        setMasterFormErrors(prev => ({
          ...prev,
          end_date: "Invoice To Date cannot be before Invoice From Date."
        }));
        
        // Still update the field value to show what user typed
        setMasterFormData(updatedData);
        return; // Stop further processing
      } else {
        // Clear the error if dates are valid
        setMasterFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.start_date;
          delete newErrors.end_date;
          return newErrors;
        });
      }
    }
  }
    let calculatedValues = {};
    let dropdownOptions = {};
    let listData = {};
    const promisesToAwait = [];
    // 1. Handle calculation rules
    for (const rule of masterFormConfig.calculationRules) {
      if (rule.triggerFields.includes(fieldName)) {
        promisesToAwait.push(
          rule.calculate({ ...updatedData }, fieldName).then((result) => {
            const [valuesFromCalc, dropdownsFromCalc, listsFromCalc] =
              result.length === 3 ? result : [result[0], result[1], {}];
            Object.assign(calculatedValues, valuesFromCalc);
            if (dropdownsFromCalc) {
              dropdownOptions = {
                ...dropdownOptions,
                ...dropdownsFromCalc,
              };
            }
            if (listsFromCalc) {
              listData = {
                ...listData,
                ...listsFromCalc,
              };
            }
          })
        );
      }
    }
    // 2. Handle direct list field dependencies not covered by calculation rules
    const fieldConfig = masterFormConfig.formFields.find(
      (f) => f.fieldName === fieldName
    );
    if (fieldConfig) {
      masterFormConfig.formFields.forEach((dependentField) => {
        const isDependency = Array.isArray(dependentField.data?.dependsOnField)
          ? dependentField.data.dependsOnField.includes(fieldName)
          : dependentField.data?.dependsOnField === fieldName;
        if (
          dependentField.tag === "list" &&
          isDependency &&
          dependentField.getRows &&
          (!listData[dependentField.fieldName] ||
            listData[dependentField.fieldName].length === 0)
        ) {
          promisesToAwait.push(
            dependentField
              .getRows(updatedData)
              .then((rows) => {
                listData = {
                  ...listData,
                  [dependentField.fieldName]: rows,
                };
              })
              .catch((error) => {
                console.error(
                  `Error fetching list rows for ${dependentField.fieldName}:`,
                  error
                );
                listData = {
                  ...listData,
                  [dependentField.fieldName]: [],
                };
              })
          );
        }
      });
    }
    await Promise.all(promisesToAwait);
    // 🔥 FIXED: Re-fetch options for dependent fields (e.g., wo_id on date change)
    if (fieldName === 'start_date' || fieldName === 'end_date') {
      const woField = masterFormConfig.formFields.find(f => f.fieldName === 'wo_id');
      if (woField?.getOptions) {
        try {
          // Pass the UPDATED data (with new date) to getOptions
          const newOptions = await woField.getOptions({ ...updatedData });
       
          // 🔥 Update separate state variable
          setWorkOrderCount(newOptions.length);
       
          console.log('🔄 [handleMasterChange] Re-fetched WO options:', newOptions.length);
       
          // Update dropdown options
          setMasterDropdownOptions(prev => ({
            ...prev,
            [woField.fieldName]: newOptions
          }));
       
        } catch (err) {
          console.error('❌ Error re-fetching WO options:', err);
        }
      }
    }
    // NEW: Check if work order field changed in edit mode
    const workOrderField = masterFormConfig.formFields.find(
      (f) => f.fieldName === "wo_id"
    );
 
    const isWorkOrderChange = workOrderField && fieldName === workOrderField.fieldName;
    const workOrderChanged = isEditMode && isWorkOrderChange &&
      masterFormData[fieldName] !== fieldValue;
    // ✅ ALWAYS reset transactions when start_date or end_date changes
    const isDateChange = fieldName === "start_date" || fieldName === "end_date";
 
    if (isDateChange && transactions.length > 0) {
      // Clear all transactions
      setTransactions([]);
      setTransactionErrors({});
      setTransactionDropdownOptions({});
      setTransactionComboboxOptions({});
      delete calculatedValues.__resetTransactions;
      showMessageBox("Invoice period dates changed. All transactions have been cleared.", "info");
    } else if (workOrderChanged) {
      // Work order changed in edit mode - clear all transactions
      setTransactions([]);
      setTransactionErrors({});
      setTransactionDropdownOptions({});
      setTransactionComboboxOptions({});
      delete calculatedValues.__resetTransactions;
      showMessageBox("Work order changed. Previous transactions have been cleared.", "info");
    } else if (calculatedValues.__resetTransactions && !isEditMode) {
      // Normal reset in create mode
      setTransactions([]);
      setTransactionErrors({});
      setTransactionDropdownOptions({});
      setTransactionComboboxOptions({});
      delete calculatedValues.__resetTransactions;
    } else if (calculatedValues.__resetTransactions) {
      delete calculatedValues.__resetTransactions;
    }
    updatedData = { ...updatedData, ...calculatedValues };
    setMasterDropdownOptions((prev) => ({
      ...prev,
      ...dropdownOptions,
    }));
    setMasterListData((prev) => ({
      ...prev,
      ...listData,
    }));
    if (name === "start_date" || name === "end_date") {
      setMasterRecordDates({
        ...masterRecordDates,
        [name]: new Date(fieldValue),
      });
    }
    setMasterFormData(updatedData);
    const dependentBooleanField = masterFormConfig.formFields.find((f) => {
      return f.dependsOnField === fieldName;
    });
    if (fieldConfig && dependentBooleanField) {
      if (type === "checkbox" && checked === false) {
        setMasterFormData((prev) => ({
          ...prev,
          [dependentBooleanField.fieldName]: null,
        }));
      }
    }
  };
  // Function to add an asset from the list to transactions
  // In form.js, find the addItemToTransaction function (around line 894)
  // Replace the entire function with this updated version:
  const addItemToTransaction = async (item) => {
    console.log('DEBUG: addItemToTransaction called with item:', item);
    const values = getDetailsFromFormFields(
      transactionFormConfig.formFields,
      masterFormData,
      transactions
    );
    let newTran = {
      ...values,
      ...values.defaultValues,
      id: Date.now(),
      dbId: null,
      [parentIdColumnName]: masterRecordId,
      inv_sr_no: transactions.length + 1,
    };
    transactionFormConfig.formFields.forEach((field) => {
      if (
        field.inputType === "number" &&
        field.readOnly !== true &&
        (newTran[field.fieldName] === undefined ||
          newTran[field.fieldName] === null ||
          isNaN(newTran[field.fieldName]))
      ) {
        newTran[field.fieldName] = 0;
      }
      if (field.identifier) {
        // Create comprehensive asset label with name, type, and identifiers
        const labelParts = [item.name];
     
        if (item.registration_number) {
          labelParts.push(item.registration_number);
        }
     
        if (item.serial_number) {
          labelParts.push(item.serial_number);
        }
     
        if (item.asset_type) {
          labelParts.push(`(${item.asset_type})`);
        }
     
        const assetLabel = labelParts.join(' | ');
     
        console.log('✅ [addItemToTransaction] Created asset label:', assetLabel);
     
        newTran[field.fieldName] = {
          id: item.asset_id,
          label: assetLabel,
          value: item.asset_id,
          name: item.name,
          asset_type: item.asset_type,
          registration_number: item.registration_number,
          serial_number: item.serial_number,
          original: {
            ...item,
            daily_rate: parseFloat(item.daily_rate || 0),
            monthly_rate: parseFloat(item.monthly_rate || 0),
            mob: parseFloat(item.mob || 0),
            demob: parseFloat(item.demob || 0),
            name: item.name,
            asset_type: item.asset_type,
            registration_number: item.registration_number,
            serial_number: item.serial_number,
            tax_type: item.tax_type,
            tax_percentage: parseFloat(item.tax_percentage || 0),
          },
        };
     
        console.log('✅ [addItemToTransaction] Set identifier:', newTran[field.fieldName]);
      } else {
        if (field.skipOriginal) return;
        if (Object.prototype.hasOwnProperty.call(item, field.fieldName)) {
          newTran[field.fieldName] = item[field.fieldName];
        }
      }
    });
    // 🔥 NEW: Apply tax percentages based on tax_type from work order transaction
    const taxPercentage = parseFloat(item.tax_percentage || 0);
    const taxType = item.tax_type || '';
    console.log('DEBUG: Tax info from item:', { taxType, taxPercentage });
    if (taxType === 'IGST') {
      // For IGST, all tax goes to IGST field
      newTran.igst_percentage = parseFloat(taxPercentage.toFixed(2)); // ✅ Ensure float
      newTran.cgst_percentage = 0.0; // ✅ Changed from 0
      newTran.sgst_percentage = 0.0; // ✅ Changed from 0
    } else if (taxType === 'CGST-SGST') {
      // For CGST-SGST, split tax equally between CGST and SGST
      newTran.cgst_percentage = parseFloat((taxPercentage / 2).toFixed(2)); // ✅ Ensure float
      newTran.sgst_percentage = parseFloat((taxPercentage / 2).toFixed(2)); // ✅ Ensure float
      newTran.igst_percentage = 0.0; // ✅ Changed from 0
    } else {
      // Default: no tax
      newTran.cgst_percentage = 0.0; // ✅ Changed from 0
      newTran.sgst_percentage = 0.0; // ✅ Changed from 0
      newTran.igst_percentage = 0.0; // ✅ Changed from 0
    }
    console.log('DEBUG: Applied tax percentages:', {
      cgst: newTran.cgst_percentage,
      sgst: newTran.sgst_percentage,
      igst: newTran.igst_percentage
    });
    let tempTranForCalc = { ...newTran };
    for (const rule of transactionFormConfig.calculationRules) {
      const ruleCalculatedValues = await rule.calculate(
        tempTranForCalc,
        null,
        masterFormData,
        transactions
      );
      Object.assign(tempTranForCalc, ruleCalculatedValues);
    }
    Object.assign(newTran, tempTranForCalc);
    const newTransactionIndex = transactions.length;
    let newTransactionDropdownOptions = {
      ...transactionDropdownOptions[newTransactionIndex],
    };
    let newTransactionComboboxOptions = {
      ...transactionComboboxOptions[newTransactionIndex],
    };
    for (const field of transactionFormConfig.formFields) {
      if (typeof field.defaultValue === "function") {
        newTran[field.fieldName] = getValueWithType(
          field,
          field.defaultValue(masterFormData, transactions)
        );
      }
      if (field.tag === "select" || field.tag === "combobox") {
        const dependsOnFieldSatisfied = Array.isArray(
          field.data?.dependsOnField
        )
          ? checkIfValuesExist(newTran, field.data.dependsOnField)
          : field.data?.dependsOnField
          ? newTran[field.data.dependsOnField]
          : true;
        if (field.getOptions && dependsOnFieldSatisfied) {
          try {
            const options = await field.getOptions(newTran, masterFormData);
            if (options) {
              if (field.tag === "select") {
                newTransactionDropdownOptions[field.fieldName] = options;
              } else if (field.tag === "combobox") {
                newTransactionComboboxOptions[field.fieldName] = options;
              }
            }
          } catch (error) {
            console.error(
              `Error fetching options for new transaction row for ${field.displayName} (getOptions):`,
              error
            );
          }
        } else if (field.data && (field.data.tableName || field.data.url)) {
          if (dependsOnFieldSatisfied) {
            try {
              const options = await getDropdownOptions(field, newTran, {});
              if (options) {
                if (field.tag === "select") {
                  newTransactionDropdownOptions[field.fieldName] = options;
                } else if (field.tag === "combobox") {
                  newTransactionComboboxOptions[field.fieldName] = options;
                }
              }
            } catch (error) {
              console.error(
                `Error fetching options for new transaction row for ${field.displayName} (getDropdownOptions):`,
                error
              );
            }
          }
        }
      }
    }
    setTransactions((prevTransactions) => {
      setTransactionErrors((prev) => ({
        ...prev,
        [prevTransactions.length]: {},
      }));
      return [...prevTransactions, newTran];
    });
    setTransactionDropdownOptions((prev) => ({
      ...prev,
      [newTransactionIndex]: newTransactionDropdownOptions,
    }));
    setTransactionComboboxOptions((prev) => ({
      ...prev,
      [newTransactionIndex]: newTransactionComboboxOptions,
    }));
  };
  const validateMasterForm = () => {
    const errors = {};
    for (const rule of masterFormConfig.validationRules) {
      const errorsFromResult = rule.validator(masterFormData, {});
      if (errorsFromResult) {
        Object.assign(errors, errorsFromResult);
      }
    }
    setMasterFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleMasterSubmit = async (e, masterdata) => {
    if (e) e.preventDefault();
    setIsLoadingMaster(true);
    if (!validateMasterForm()) {
      showMessageBox("Please correct the highlighted fields.", "error");
      setIsLoadingMaster(false);
      return { success: false, message: "Master form validation failed." };
    }
    try {
      const payload = { ...masterFormData, ...masterdata };
   
      // ✅ FIXED: Convert dates without timezone shift
      Object.keys(payload).forEach((key) => {
        const fieldConfig = masterFormConfig.formFields.find(
          (f) => f.fieldName === key
        );

        // Handle date fields - preserve the user-selected date
        if (fieldConfig && fieldConfig.inputType === "date" && payload[key]) {
          const dateStr = payload[key].split('T')[0]; // Get YYYY-MM-DD only
          
          if (key.includes("start")) {
            // Start dates: set to 00:00:00 UTC
            payload[key] = `${dateStr}T00:00:00.000Z`;
          } else if (key.includes("end")) {
            // End dates: set to 23:59:59.999 UTC
            payload[key] = `${dateStr}T23:59:59.999Z`;
          } else {
            // Other dates (inv_date, wo_date): set to 00:00:00 UTC
            payload[key] = `${dateStr}T00:00:00.000Z`;
          }
        }
     
        // Rest of the existing cleanup code
        if (typeof payload[key] === "string" && payload[key].trim() === "") {
          payload[key] = null;
        } else if (typeof payload[key] === "number" && isNaN(payload[key])) {
          payload[key] = null;
        }
     
        if (fieldConfig && fieldConfig.isMulti && Array.isArray(payload[key])) {
          payload[key] = payload[key].map((elem) => {
            return fieldConfig.send_value === true ? elem.label : elem.value;
          });
        } else if (typeof payload[key] === "object" && payload[key] !== null) {
          payload[key] = payload[key].id;
        }
     
        if (fieldConfig && fieldConfig.tag === "list") {
          delete payload[key];
        }
      });
      const method = masterRecordId ? "PUT" : "POST";
      const url = masterRecordId
        ? `${masterFormConfig.submitApi}/${masterRecordId}`
        : masterFormConfig.submitApi;
      const token = localStorage.getItem("zTaskAuthToken");
      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        const result = await response.json();
        const masterId = result.id || masterRecordId;
        const docNo = result.doc_no || masterFormData.doc_no;
        if (selectedFiles.length > 0) {
          try {
            await uploadAttachments(
              masterId,
              moduleName,
              selectedFiles,
              fileDescriptions
            );
            setSelectedFiles([]);
            setFileDescriptions([]);
            const attRes = await fetch(
              `${ATTACHMENT_API_URL}/attachment/api/attachments/${masterId}/${moduleName}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            if (attRes.ok) {
              const attachments = await attRes.json();
              setExistingAttachments(attachments.filter((att) => att.active));
            }
          } catch (uploadError) {
            showMessageBox(
              `Master saved, but file upload failed: ${uploadError.message}`,
              "error"
            );
          }
        }
        if (!submitTogether)
          showMessageBox(
            `${masterFormConfig.masterFormHeading} ${
              masterId ? "updated" : "created"
            } successfully!`,
            "success"
          );
        setMasterRecordId(masterId);
        setMasterFormData(payload);
        setMasterRecordDates({
          start_date: payload.start_date ? new Date(payload.start_date) : null,
          end_date: payload.end_date ? new Date(payload.end_date) : null,
        });
        setMasterFormErrors({});
        if (onFormSuccess) onFormSuccess(masterId);
        return { success: true, masterId: masterId, docNo: docNo };
      } else {
        const errorData = await response.json();
        console.error("API Error:", errorData);
        setMasterFormErrors(errorData.fields || {});
        showMessageBox(
          errorData.error || "An unexpected error occurred.",
          "error"
        );
        return { success: false, errors: errorData.fields };
      }
    } catch (error) {
      console.error("Network or unexpected error:", error);
      showMessageBox(
        "Failed to connect to the server. Please check your internet connection.",
        "error"
      );
      return { success: false, message: "Network error." };
    } finally {
      setIsLoadingMaster(false);
    }
  };
  // In form.js - Replace your handleTransactionChange function with this:

const handleTransactionChange = async (index, e) => {
  console.log('DEBUG: handleTransactionChange called - index:', index, 'event:', e);
  const { name, value, type, checked } = e.target || e;
  const updatedTransactions = [...transactions];
  let currentTransaction = { ...updatedTransactions[index] };
  
  const fieldConfig = transactionFormConfig.formFields.find(
    (f) => f.fieldName === name
  );
  
  let parsedValue = value;
  currentTransaction[name] =
    type === "checkbox" ? checked : value === "" ? null : value;
  
  if (type === "number") {
    parsedValue = value === "" ? null : parseFloat(value);
    currentTransaction[name] = parsedValue;
    console.log('DEBUG: Parsed number value for', name, ':', parsedValue);
  }
  
  console.log('DEBUG: Updated transaction[name]:', currentTransaction[name]);
  
  // 🔥 FIX: Skip all processing for remarks field - just update and return
  if (name === "remarks") {
    updatedTransactions[index] = currentTransaction;
    setTransactions(updatedTransactions);
    console.log('DEBUG: Remarks field - skipping calculations');
    return;
  }
  
  if (fieldConfig) {
    for (const dependentField of transactionFormConfig.formFields) {
      const isDependency = Array.isArray(dependentField.data?.dependsOnField)
        ? dependentField.data.dependsOnField.includes(name)
        : dependentField.data?.dependsOnField === name;
      
      if (isDependency) {
        const dependsOnFieldSatisfied = Array.isArray(
          dependentField.data?.dependsOnField
        )
          ? checkIfValuesExist(
              currentTransaction,
              dependentField.data.dependsOnField
            )
          : dependentField.data?.dependsOnField
          ? currentTransaction[dependentField.data.dependsOnField]
          : true;
        
        if (
          (dependentField.tag === "select" ||
            dependentField.tag === "combobox") &&
          dependsOnFieldSatisfied
        ) {
          let options = [];
          try {
            if (dependentField.getOptions) {
              options = await dependentField.getOptions(
                currentTransaction,
                masterFormData
              );
            } else if (
              dependentField.data &&
              (dependentField.data.tableName || dependentField.data.url)
            ) {
              options = await getDropdownOptions(
                dependentField,
                currentTransaction,
                {}
              );
            }
            console.log('DEBUG: Fetched options for dependent field', dependentField.fieldName, ':', options.length, 'options');
          } catch (error) {
            console.error(
              `Error fetching options for transaction row ${index} for ${dependentField.displayName}:`,
              error
            );
          }
          
          if (dependentField.tag === "select") {
            setTransactionDropdownOptions((prev) => ({
              ...prev,
              [index]: {
                ...prev[index],
                [dependentField.fieldName]: options,
              },
            }));
          } else if (dependentField.tag === "combobox") {
            setTransactionComboboxOptions((prev) => ({
              ...prev,
              [index]: {
                ...prev[index],
                [dependentField.fieldName]: options,
              },
            }));
          }
        }
        
        if (dependentField.getValue && dependsOnFieldSatisfied) {
          try {
            const valueFromGetValue = await dependentField.getValue(
              currentTransaction,
              masterFormData
            );
            currentTransaction[dependentField.fieldName] = valueFromGetValue;
            console.log('DEBUG: Set value from getValue for', dependentField.fieldName, ':', valueFromGetValue);
          } catch (error) {
            console.error(
              `Error executing getValue for transaction row ${index} for ${dependentField.displayName}:`,
              error
            );
          }
        }
      }
    }
  }
  
  // Run calculation rules ONLY if the changed field is in triggerFields
  let tempTransaction = { ...currentTransaction };
  let shouldRunCalculations = false;
  
  for (const rule of transactionFormConfig.calculationRules) {
    if (rule.triggerFields.includes(name)) {
      shouldRunCalculations = true;
      break;
    }
  }
  
  if (shouldRunCalculations) {
    console.log('DEBUG: Running calculations for field:', name);
    try {
      for (const rule of transactionFormConfig.calculationRules) {
        if (rule.triggerFields.includes(name)) {
          const ruleResult = await rule.calculate(
            tempTransaction,
            name,
            masterFormData,
            updatedTransactions
          );
          Object.assign(tempTransaction, ruleResult);
          console.log('DEBUG: Calculation rule applied for', name, 'result:', ruleResult);
        }
      }
    } catch (calcError) {
      console.error('DEBUG: Error in calculations:', calcError);
    }
  } else {
    console.log('DEBUG: Skipping calculations - field not in triggerFields:', name);
  }
  
  // Update transaction
  updatedTransactions[index] = tempTransaction;
  console.log('DEBUG: Final updated transaction:', updatedTransactions[index]);
  
  // Validate the transaction
  const newErrors = {};
  for (const rule of transactionFormConfig.validationRules) {
    const errorsFromResult = rule.validator(
      tempTransaction,
      {},
      updatedTransactions,
      index,
      masterRecordDates,
      masterFormData
    );
    if (errorsFromResult) {
      Object.assign(newErrors, errorsFromResult);
    }
  }
  
  const uniquenessErrors = validateInvoiceForUniqueness(
    tempTransaction,
    updatedTransactions,
    index,
    transactionFormConfig
  );
  Object.assign(newErrors, uniquenessErrors);
  
  setTransactionErrors((prev) => ({ ...prev, [index]: newErrors }));
  setTransactions(updatedTransactions);
  console.log('DEBUG: State updated for transactions');
};


  const validateSingleTransaction = (tran, index) => {
    const errors = {};
    for (const rule of transactionFormConfig.validationRules) {
      const errorsFromResult = rule.validator(
        tran,
        {},
        transactions,
        index,
        masterRecordDates,
        masterFormData
      );
      if (errorsFromResult) {
        Object.assign(errors, errorsFromResult);
      }
    }
    const uniquenessErrors = validateInvoiceForUniqueness(
      tran,
      transactions,
      index,
      transactionFormConfig
    );
    Object.assign(errors, uniquenessErrors);
    setTransactionErrors((prev) => ({ ...prev, [index]: errors }));
    return Object.keys(errors).length === 0;
  };
  const saveSingleTransaction = async (tranToSave, index, masterIdForLink) => {
    const linkedMasterId = masterIdForLink || masterRecordId;
    if (!linkedMasterId && !submitTogether) {
      showMessageBox("Master record must be created first.", "error");
      return { success: false, message: "Master record not null." };
    }
    if (!validateSingleTransaction(tranToSave, index)) {
      return {
        success: false,
        message: "Validation failed for transaction.",
        errors: transactionErrors[index],
      };
    }
    try {
      const cleanedPayload = {};
      const transactionFieldNames = transactionFormConfig.formFields.map(
        f => f.fieldName
      );
      cleanedPayload[parentIdColumnName] = linkedMasterId;
      for (const fieldName of transactionFieldNames) {
        if (Object.prototype.hasOwnProperty.call(tranToSave, fieldName)) {
          cleanedPayload[fieldName] = tranToSave[fieldName];
        }
      }
      if (!cleanedPayload.inv_sr_no) {
        cleanedPayload.inv_sr_no = tranToSave.inv_sr_no || (index + 1);
      }
      // 🔥 CRITICAL: Explicitly ensure tax percentages are floats
      const taxFields = ['cgst_percentage', 'sgst_percentage', 'igst_percentage'];
      taxFields.forEach(field => {
        if (field in cleanedPayload) {
          const value = cleanedPayload[field];
          // ✅ Convert any truthy/falsy to proper number
          if (typeof value === 'boolean') {
            cleanedPayload[field] = 0.00;
          } else {
            cleanedPayload[field] = value === 0 ? 0.00 : parseFloat(Number(value).toFixed(2));
          }
          console.log(`🔍 [Save] ${field}:`, cleanedPayload[field], 'Type:', typeof cleanedPayload[field]);
        }
      });
      // ✅ FIXED: Convert dates without timezone shift
      Object.keys(cleanedPayload).forEach((key) => {
        const fieldConfig = transactionFormConfig.formFields.find(
          f => f.fieldName === key
        );

        // Convert date fields - preserve the user-selected date
        if (fieldConfig && fieldConfig.inputType === "date" && cleanedPayload[key]) {
          const dateStr = cleanedPayload[key].split('T')[0]; // Get YYYY-MM-DD only
          
          if (key.includes("start")) {
            // Start dates: set to 00:00:00 UTC
            cleanedPayload[key] = `${dateStr}T00:00:00.000Z`;
          } else if (key.includes("end")) {
            // End dates: set to 23:59:59.999 UTC
            cleanedPayload[key] = `${dateStr}T23:59:59.999Z`;
          } else {
            // Other dates: set to 00:00:00 UTC
            cleanedPayload[key] = `${dateStr}T00:00:00.000Z`;
          }
        }   
     
        // Rest of cleanup
        if (typeof cleanedPayload[key] === "string" && cleanedPayload[key].trim() === "") {
          cleanedPayload[key] = null;
        } else if (typeof cleanedPayload[key] === "number" && isNaN(cleanedPayload[key])) {
          cleanedPayload[key] = null;
        }
     
        if (typeof cleanedPayload[key] === "object" && cleanedPayload[key] !== null) {
          cleanedPayload[key] = cleanedPayload[key].id;
        }
      });
      const method = tranToSave.dbId ? "PUT" : "POST";
      const url = tranToSave.dbId
        ? `${transactionFormConfig.submitApi}/${tranToSave.dbId}`
        : transactionFormConfig.submitApi;
      const token = localStorage.getItem("zTaskAuthToken");
      console.log('🔍 [Transaction API] Final payload being sent:', JSON.stringify(cleanedPayload, null, 2));
      console.log('🔍 [Transaction API] Tax field types:', {
        cgst: typeof cleanedPayload.cgst_percentage,
        sgst: typeof cleanedPayload.sgst_percentage,
        igst: typeof cleanedPayload.igst_percentage
      });
      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(cleanedPayload),
      });
      if (response.ok) {
        const result = await response.json();
        console.log('✅ [Transaction API] Success:', result);
     
        if (!tranToSave.dbId) {
          setTransactions((prev) =>
            prev.map((t, i) =>
              i === index
                ? {
                    ...t,
                    dbId: result.id || t.dbId,
                    [parentIdColumnName]: linkedMasterId,
                  }
                : t
            )
          );
        }
        setTransactionErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[index];
          return newErrors;
        });
        return {
          success: true,
          message: "Transaction saved successfully.",
          transactionId: result.id,
        };
      } else {
        const errorData = await response.json();
        console.error('❌ [Transaction API] Error response:', errorData);
     
        // ✅ NEW: Check for duplicate error and display it prominently
        if (errorData.error && errorData.error.includes('invoice already exists')) {
          showMessageBox(errorData.error, "error");
          setTransactionErrors((prev) => ({
            ...prev,
            [index]: {
              general: errorData.error,
              asset_id: "Duplicate transaction detected",
              invoice_for: "This asset and invoice type combination already exists"
            },
          }));
        } else {
          setTransactionErrors((prev) => ({
            ...prev,
            [index]: {
              ...(prev[index] || {}),
              ...errorData.fields,
              general: errorData.error
            },
          }));
        }
     
        return {
          success: false,
          message: errorData.error || "API error during save.",
          errors: errorData.fields,
        };
      }
    } catch (error) {
      console.error('❌ [Transaction API] Network Error:', error);
      setTransactionErrors((prev) => ({
        ...prev,
        [index]: { ...(prev[index] || {}), general: 'Network error.' },
      }));
      return { success: false, message: "Network error." };
    }
  };
  const addTransaction = async () => {
    const transactionDefaultFieldValues = getDetailsFromFormFields(
      transactionFormConfig.formFields,
      masterFormData,
      transactions
    );
    setTransactions((prevTransactions) => {
      let newTran = {
        ...transactionDefaultFieldValues,
        ...transactionDefaultFieldValues.defaultValues,
        id: Date.now(),
        dbId: null,
        [parentIdColumnName]: masterRecordId,
        inv_sr_no: prevTransactions.length + 1,
      };
      transactionFormConfig.formFields.forEach((field) => {
        if (
          field.inputType === "number" &&
          (newTran[field.fieldName] === undefined ||
            newTran[field.fieldName] === null)
        ) {
          newTran[field.fieldName] = 0;
        }
      });
      setTransactionErrors((prev) => ({
        ...prev,
        [prevTransactions.length]: {},
      }));
      return [...prevTransactions, newTran];
    });
  };
  const removeTransaction = async (indexToRemove, dbId) => {
    if (dbId) {
      const confirmDelete = await new Promise((resolve) => {
        const alertBox = document.createElement("div");
        alertBox.className = `custom-alert custom-alert-confirm`;
        alertBox.innerHTML = `
          <div class="custom-alert-content">
            <p>Are you sure you want to delete this record?</p>
            <button id="confirm-yes">Yes</button>
            <button id="confirm-no">No</button>
          </div>
        `;
        document.body.appendChild(alertBox);
        document.getElementById("confirm-yes").onclick = () => {
          alertBox.remove();
          resolve(true);
        };
        document.getElementById("confirm-no").onclick = () => {
          alertBox.remove();
          resolve(false);
        };
      });
      if (!confirmDelete) {
        return;
      }
      try {
        const token = localStorage.getItem("zTaskAuthToken");
        const response = await fetch(
          `${transactionFormConfig.submitApi}/${dbId}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to delete transaction.");
        }
        showMessageBox("Transaction deleted successfully.", "success");
      } catch (error) {
        console.error("Error deleting transaction:", error);
        showMessageBox(
          `Failed to delete transaction: ${error.message}`,
          "error"
        );
        return;
      }
    }
    setTransactions((prevTransactions) => {
      const updated = prevTransactions.filter((_, i) => i !== indexToRemove);
      return updated.map((tran, index) => ({
        ...tran,
        inv_sr_no: index + 1,
      }));
    });
    setTransactionErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[indexToRemove];
      return newErrors;
    });
    setTransactionComboboxOptions((prev) => {
      const newOptions = { ...prev };
      delete newOptions[indexToRemove];
      return newOptions;
    });
    if (!submitTogether) {
      await handleMasterSubmit({ preventDefault: () => {} });
    }
  };
// ========================================
// 1. ADD THIS NEW FUNCTION in Form component (after removeTransaction function)
// ========================================
const duplicateAssetTransaction = async (sourceIndex) => {
  const sourceTran = transactions[sourceIndex];
  // Get the asset ID
  const assetId = sourceTran.asset_id?.id || sourceTran.asset_id;
  if (!assetId) {
    showMessageBox("Cannot duplicate: No asset selected.", "error");
    return;
  }
  // Get all invoice_for values already used for this asset
  const usedInvoiceForValues = transactions
    .filter(t => {
      const tAssetId = t.asset_id?.id || t.asset_id;
      return tAssetId === assetId;
    })
    .map(t => String(t.invoice_for || '').toLowerCase())
    .filter(Boolean);
  console.log('Used invoice_for values for asset:', usedInvoiceForValues);
  // Fetch available invoice_for options
  const invoiceForField = transactionFormConfig.formFields.find(
    f => f.fieldName === 'invoice_for'
  );
  if (!invoiceForField?.getOptions) {
    showMessageBox("Cannot determine available invoice types.", "error");
    return;
  }
  let availableOptions = [];
  try {
    const allOptions = await invoiceForField.getOptions(sourceTran, masterFormData);
 
    // Filter out already used options
    availableOptions = allOptions.filter(opt =>
      !usedInvoiceForValues.includes(String(opt.value || opt.id || '').toLowerCase())
    );
 
    console.log('Available invoice_for options:', availableOptions);
 
    if (availableOptions.length === 0) {
      showMessageBox(
        "All invoice types for this asset have already been added.",
        "warning"
      );
      return;
    }
  } catch (error) {
    console.error('Error fetching invoice_for options:', error);
    showMessageBox("Failed to fetch available invoice types.", "error");
    return;
  }
  // Create new transaction with same asset
  const values = getDetailsFromFormFields(
    transactionFormConfig.formFields,
    masterFormData,
    transactions
  );
  let newTran = {
    ...values,
    ...values.defaultValues,
    id: Date.now(),
    dbId: null,
    [parentIdColumnName]: masterRecordId,
    inv_sr_no: transactions.length + 1,
  };
  // Copy asset data from source transaction
  newTran.asset_id = sourceTran.asset_id;
  // Copy dates and other relevant fields
  newTran.start_date = sourceTran.start_date;
  newTran.end_date = sourceTran.end_date;
  newTran.hsn_code = sourceTran.hsn_code;
  // Copy tax percentages
  newTran.cgst_percentage = sourceTran.cgst_percentage;
  newTran.sgst_percentage = sourceTran.sgst_percentage;
  newTran.igst_percentage = sourceTran.igst_percentage;
  // DO NOT copy invoice_for - it must be unique
  newTran.invoice_for = null;
  // Reset calculated fields
  newTran.quantity = 0;
  newTran.rate = 0;
  newTran.unit = null;
  newTran.taxable_amount = 0;
  newTran.cgst_amount = 0;
  newTran.sgst_amount = 0;
  newTran.igst_amount = 0;
  newTran.amount = 0;
  // Initialize number fields
  transactionFormConfig.formFields.forEach((field) => {
    if (
      field.inputType === "number" &&
      field.readOnly !== true &&
      (newTran[field.fieldName] === undefined ||
        newTran[field.fieldName] === null ||
        isNaN(newTran[field.fieldName]))
    ) {
      newTran[field.fieldName] = 0;
    }
  });
  const newTransactionIndex = transactions.length;
  // Fetch dropdown and combobox options for new row
  let newTransactionDropdownOptions = {};
  let newTransactionComboboxOptions = {};
  for (const field of transactionFormConfig.formFields) {
    if (field.tag === "select" || field.tag === "combobox") {
      const dependsOnFieldSatisfied = Array.isArray(field.data?.dependsOnField)
        ? checkIfValuesExist(newTran, field.data.dependsOnField)
        : field.data?.dependsOnField
        ? newTran[field.data.dependsOnField]
        : true;
      if (field.getOptions && dependsOnFieldSatisfied) {
        try {
          const options = await field.getOptions(newTran, masterFormData);
          if (options) {
            // For invoice_for field, use filtered available options
            if (field.fieldName === 'invoice_for') {
              if (field.tag === "select") {
                newTransactionDropdownOptions[field.fieldName] = availableOptions;
              } else if (field.tag === "combobox") {
                newTransactionComboboxOptions[field.fieldName] = availableOptions;
              }
            } else {
              if (field.tag === "select") {
                newTransactionDropdownOptions[field.fieldName] = options;
              } else if (field.tag === "combobox") {
                newTransactionComboboxOptions[field.fieldName] = options;
              }
            }
          }
        } catch (error) {
          console.error(
            `Error fetching options for new transaction row for ${field.displayName}:`,
            error
          );
        }
      } else if (field.data && (field.data.tableName || field.data.url)) {
        if (dependsOnFieldSatisfied) {
          try {
            const options = await getDropdownOptions(field, newTran, {});
            if (options) {
              if (field.tag === "select") {
                newTransactionDropdownOptions[field.fieldName] = options;
              } else if (field.tag === "combobox") {
                newTransactionComboboxOptions[field.fieldName] = options;
              }
            }
          } catch (error) {
            console.error(
              `Error fetching options for new transaction row for ${field.displayName}:`,
              error
            );
          }
        }
      }
    }
  }
  setTransactions((prevTransactions) => {
    setTransactionErrors((prev) => ({
      ...prev,
      [prevTransactions.length]: {},
    }));
    return [...prevTransactions, newTran];
  });
  setTransactionDropdownOptions((prev) => ({
    ...prev,
    [newTransactionIndex]: newTransactionDropdownOptions,
  }));
  setTransactionComboboxOptions((prev) => ({
    ...prev,
    [newTransactionIndex]: newTransactionComboboxOptions,
  }));
  // showMessageBox(
  // `New transaction row added for the same asset. Please select a different Invoice Type.`,
  // "success"
  // );
};
  const saveAllTransactions = async (masterIdForLink, newTransactions) => {
    const payload = newTransactions && newTransactions.length > 0
      ? newTransactions
      : transactions;
    console.log('💾 [Save All Trans] Called with masterIdForLink:', masterIdForLink);
    console.log('💾 [Save All Trans] Using transactions:', newTransactions ? 'NEW calculated transactions' : 'EXISTING transactions');
    console.log('💾 [Save All Trans] Total payload:', payload.length);
    if (payload.length === 0) {
      console.warn('⚠️ [Save All Trans] No transactions to save');
      showMessageBox("No transaction to save.", "info");
      return true;
    }
    setIsLoadingTransactions(true);
 
    let allSucceeded = true;
    const errorsDuringSave = {};
 
    for (let i = 0; i < payload.length; i++) {
      const tran = payload[i];
      console.log(`💾 [Save All Trans] Saving transaction ${i + 1}/${payload.length}:`, {
        id: tran.id,
        dbId: tran.dbId,
        asset_id: tran.asset_id,
        inv_sr_no: tran.inv_sr_no
      });
   
      const result = await saveSingleTransaction(tran, i, masterIdForLink);
   
      if (!result.success) {
        allSucceeded = false;
        errorsDuringSave[i] = {
          ...errorsDuringSave[i],
          general: result.message,
          ...result.errors,
        };
        console.error(`❌ [Save All Trans] Transaction ${i + 1} failed:`, result);
      } else {
        console.log(`✅ [Save All Trans] Transaction ${i + 1} saved successfully`);
      }
    }
 
    setTransactionErrors((prev) => ({ ...prev, ...errorsDuringSave }));
    setIsLoadingTransactions(false);
    if (allSucceeded) {
      console.log('✅ [Save All Trans] All transactions saved successfully');
      showMessageBox("All transactions saved successfully!", "success");
    } else {
      console.error('❌ [Save All Trans] Some transactions failed');
      showMessageBox(
        "Some transactions failed to save. Please correct the highlighted rows.",
        "error"
      );
    }
 
    console.log('💾 [Save All Trans] Returning:', allSucceeded);
    return allSucceeded;
  };
  const handleCombinedSubmit = async (e) => {
    e.preventDefault();
 
    if (isLoadingMaster || isLoadingTransactions) {
      console.log('⏳ [Submit] Already submitting, ignoring duplicate call');
      return;
    }
 
    setIsLoadingMaster(true);
    console.log('🚀 [Combined Submit] Starting submission process');
    const isMasterValid = validateMasterForm();
    if (!isMasterValid) {
      console.error('❌ [Combined Submit] Master validation failed:', masterFormErrors);
      showMessageBox(
        "Please correct the highlighted fields in the master form.",
        "error"
      );
      setIsLoadingMaster(false);
      return;
    }
    console.log('✅ [Combined Submit] Master validation passed');
    if (transactions.length === 0) {
      console.error('❌ [Combined Submit] No transactions found');
      showMessageBox(
        "Please also add the transactions before submitting.",
        "error"
      );
      setIsLoadingMaster(false);
      return;
    }
    console.log(`✅ [Combined Submit] Found ${transactions.length} transaction(s)`);
    let allTransactionsValid = true;
    const newTransactionErrors = {};
    for (let i = 0; i < transactions.length; i++) {
      const tran = transactions[i];
      const isValid = validateSingleTransaction(tran, i);
      if (!isValid) {
        allTransactionsValid = false;
        Object.assign(newTransactionErrors, { [i]: transactionErrors[i] });
        console.error(`❌ [Combined Submit] Transaction ${i} validation failed:`, transactionErrors[i]);
      }
    }
    if (!allTransactionsValid) {
      console.error('❌ [Combined Submit] Transaction validation failed:', newTransactionErrors);
      showMessageBox(
        "Please correct the highlighted fields in the transaction details.",
        "error"
      );
      setTransactionErrors((prev) => ({ ...prev, ...newTransactionErrors }));
      setIsLoadingMaster(false);
      return;
    }
    console.log('✅ [Combined Submit] All transactions validated');
    try {
      console.log('📊 [Combined Submit] Running master calculations...');
      const [newMasterData, newTranData] = await masterCalculatedFieldsFn(
        masterFormData,
        transactions
      );
   
      console.log('📊 [Combined Submit] Calculated master data:', newMasterData);
      console.log('📊 [Combined Submit] Calculated tran data:', newTranData);
      console.log('💾 [Combined Submit] Saving master record...');
      const masterSaveResult = await handleMasterSubmit(
        { preventDefault: () => {} },
        newMasterData
      );
   
      console.log('💾 [Combined Submit] Master save result:', masterSaveResult);
   
      if (!masterSaveResult.success) {
        console.error('❌ [Combined Submit] Master save failed:', masterSaveResult);
        showMessageBox("Failed to save master record.", "error");
        setIsLoadingMaster(false);
        return;
      }
      const masterId = masterSaveResult.masterId;
      setMasterRecordId(masterId);
      console.log('✅ [Combined Submit] Master saved with ID:', masterId);
      console.log('💾 [Combined Submit] Saving transactions...');
      const allTransactionsSaved = await saveAllTransactions(
        masterId,
        newTranData
      );
   
      console.log('💾 [Combined Submit] Transactions save result:', allTransactionsSaved);
   
      if (!allTransactionsSaved) {
        console.error('❌ [Combined Submit] Some transactions failed to save');
        showMessageBox("Failed to save all transactions.", "error");
        setIsLoadingMaster(false);
        return;
      }
      console.log('✅ [Combined Submit] All transactions saved successfully');
      console.log('🎉 [Combined Submit] COMPLETE - Showing success message');
   
      showMessageBox("Master and Transactions saved successfully!", "success");
   
      if (onFormSuccess) {
        console.log('📞 [Combined Submit] Calling onFormSuccess callback');
        onFormSuccess(masterId);
      }
   
      // ✅ Navigate back to invoice list after successful submission
      setTimeout(() => {
        navigate('/invoices');
      }, 1500);
   
    } catch (error) {
      console.error("❌ [Combined Submit] Unexpected error:", error);
      console.error("❌ [Combined Submit] Error stack:", error.stack);
      showMessageBox(
        "An unexpected error occurred during combined submission.",
        "error"
      );
    } finally {
      console.log('🏁 [Combined Submit] Finished - resetting loading state');
      setIsLoadingMaster(false);
    }
  };
const handleSendForChecking = async (statusConfig) => {
  if (!masterRecordId) {
    showMessageBox("Please save the invoice first before sending for checking.", "error");
    return;
  }
  if (checkers.length === 0) {
    showMessageBox("Please select a checker.", "error");
    return;
  }
  
  try {
    setIsLoadingMaster(true);
    
    const token = localStorage.getItem("zTaskAuthToken");
    
    // ✅ Validate checker IDs
    const checkerIds = checkers
      .map((c, index) => {
        if (typeof c === 'number' && !isNaN(c)) {
          return c;
        }
        if (typeof c === 'object' && c !== null && c.id !== undefined && c.id !== null) {
          const parsed = parseInt(c.id, 10);
          if (!isNaN(parsed)) return parsed;
        }
        if (typeof c === 'string') {
          const parsed = parseInt(c, 10);
          if (!isNaN(parsed)) return parsed;
        }
        return null;
      })
      .filter(id => id !== null && !isNaN(id) && id > 0);
    
    if (checkerIds.length === 0) {
      showMessageBox("Invalid checker selection. Please try again.", "error");
      setIsLoadingMaster(false);
      return;
    }
    
    // ✅ CRITICAL FIX: Use CURRENT stage (not always Draft)
    const currentStatusValue = masterFormData[statusField];
    
    // Determine correct stage based on current status
    let stage, remarks;
    
    if (currentStatusValue === "Drafted") {
      // New draft → Check Invoice
      stage = "Draft";
      remarks = "Sending for checking";
    } else if (currentStatusValue === "Rejected by Checker") {
      // Resubmitting after checker rejection → Check Invoice
      stage = "Check Invoice";
      remarks = "Resubmitting after checker rejection";
    } else if (currentStatusValue === "Rejected by Approver") {
      // Resubmitting after approver rejection → Check Invoice  
      stage = "Check Invoice";
      remarks = "Resubmitting after approver rejection";
    } else {
      // Fallback
      stage = "Draft";
      remarks = "Sending for checking";
    }
    
    const payload = {
      stage: stage,           // ✅ FIXED: Use determined stage
      to: "approved",
      remarks: remarks,
      approvers: checkerIds
    };
    
    console.log('📧 [Send for Checking] Payload:', JSON.stringify(payload, null, 2));
    console.log('📧 [Send for Checking] Current Status:', currentStatusValue);
    console.log('📧 [Send for Checking] Determined Stage:', stage);
    
    const response = await fetch(
      `${ATTACHMENT_API_URL}/invoice/api/invoices/status/${masterRecordId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      }
    );
    
    if (response.ok) {
      const result = await response.json();
      showMessageBox("Invoice sent for checking successfully!", "success");
      
      setMasterFormData(prev => ({
        ...prev,
        current_status: result.current_status,
        stage: result.stage,
        status_sequence: result.status_sequence,
        inv_check_status: result.inv_check_status,
        inv_check_remark: result.inv_check_remark
      }));
      
      setCheckers([]);
      
      setTimeout(() => {
        navigate('/invoices');
      }, 1500);
      
    } else {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText };
      }
      
      showMessageBox(
        errorData.error || "Failed to send invoice for checking.",
        "error"
      );
    }
  } catch (error) {
    console.error("❌ [Send for Checking] Network error:", error);
    showMessageBox(
      "Failed to send invoice for checking. Please try again.",
      "error"
    );
  } finally {
    setIsLoadingMaster(false);
  }
};
    
    
    
const handleCheckerApproveForApproval = async (statusConfig) => {
  if (!masterRecordId) {
    showMessageBox("Invalid invoice.", "error");
    return;
  }
  if (approvers.length === 0) {
    showMessageBox("Please select at least one approver.", "error");
    return;
  }
  
  try {
    setIsLoadingMaster(true);
    
    const token = localStorage.getItem("zTaskAuthToken");
    
    // ✅ CRITICAL: Validate approver IDs
    const approverIds = approvers
      .map(a => {
        if (typeof a === 'number') return a;
        if (typeof a === 'object' && a !== null && a.id) return parseInt(a.id, 10);
        const parsed = parseInt(a, 10);
        if (!isNaN(parsed)) return parsed;
        console.error('Invalid approver format:', a);
        return null;
      })
      .filter(id => id !== null && !isNaN(id));
    
    if (approverIds.length === 0) {
      showMessageBox("Invalid approver selection. Please try again.", "error");
      setIsLoadingMaster(false);
      return;
    }
    
    console.log('📧 [Checker Approve] Sending with approvers:', approverIds);
    
    // ✅ Use correct stage transition
    const response = await fetch(
      `${ATTACHMENT_API_URL}/invoice/api/invoices/status/${masterRecordId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          stage: "Check Invoice",           // ✅ Current stage
          to: "approved",                   // ✅ Action to take
          remarks: "Check approved, sending for final approval",
          approvers: approverIds            // ✅ Approver IDs for next stage
        }),
      }
    );
    
    console.log('📧 [Checker Approve] Response status:', response.status);
    
    if (response.ok) {
      const result = await response.json();
      console.log('✅ [Checker Approve] Success:', result);
      
      showMessageBox("Invoice approved by checker and sent for approval!", "success");
      
      // ✅ Update form data with new status
      setMasterFormData(prev => ({
        ...prev,
        current_status: result.current_status || "Waiting for approval",
        stage: result.stage || "Approval",
        status_sequence: result.status_sequence || 3,
        inv_check_status: "Check Approved",
        inv_approval_status: "Waiting for approval"
      }));
      
      // Clear approvers selection
      setApprovers([]);
      
      // Navigate back after short delay
      setTimeout(() => {
        navigate('/invoices');
      }, 1500);
      
    } else {
      const errorText = await response.text();
      console.error("❌ [Checker Approve] Error response:", errorText);
      
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText };
      }
      
      showMessageBox(
        errorData.error || "Failed to approve and send for approval.",
        "error"
      );
    }
  } catch (error) {
    console.error("❌ [Checker Approve] Network error:", error);
    console.error("❌ [Checker Approve] Error stack:", error.stack);
    showMessageBox(
      "Failed to approve invoice. Please try again.",
      "error"
    );
  } finally {
    setIsLoadingMaster(false);
  }
};

// ✅ NEW: Handler for final approval by approver
const handleApproverApprove = async () => {
  if (!masterRecordId) {
    showMessageBox("Invalid invoice.", "error");
    return;
  }
  
  try {
    setIsLoadingMaster(true);
    
    const token = localStorage.getItem("zTaskAuthToken");
    
    console.log('✅ [Approver Approve] Approving invoice:', masterRecordId);
    
    const response = await fetch(
      `${ATTACHMENT_API_URL}/invoice/api/invoices/status/${masterRecordId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          stage: "Approval",              // ✅ Current stage
          to: "approved",                 // ✅ Action to take
          remarks: "Invoice approved",
        }),
      }
    );
    
    console.log('✅ [Approver Approve] Response status:', response.status);
    
    if (response.ok) {
      const result = await response.json();
      console.log('✅ [Approver Approve] Success:', result);
      
      showMessageBox("Invoice approved successfully!", "success");
      
      // ✅ Update form data with new status
      setMasterFormData(prev => ({
        ...prev,
        current_status: result.current_status || "Approved",
        stage: result.stage || "Approved",
        status_sequence: result.status_sequence || 4,
        inv_approval_status: "Approved"
      }));
      
      // Navigate back after short delay
      setTimeout(() => {
        navigate('/invoices');
      }, 1500);
      
    } else {
      const errorText = await response.text();
      console.error("❌ [Approver Approve] Error response:", errorText);
      
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText };
      }
      
      showMessageBox(
        errorData.error || "Failed to approve invoice.",
        "error"
      );
    }
  } catch (error) {
    console.error("❌ [Approver Approve] Network error:", error);
    console.error("❌ [Approver Approve] Error stack:", error.stack);
    showMessageBox(
      "Failed to approve invoice. Please try again.",
      "error"
    );
  } finally {
    setIsLoadingMaster(false);
  }
};


  const handlePrintInvoice = async () => {
    if (!masterRecordId) {
      showMessageBox("No invoice to print.", "error");
      return;
    }
    try {
      const printUrl = `${window.location.origin}/invoice/print/${masterRecordId}`;
      window.open(printUrl, '_blank');
      showMessageBox("Opening print preview...", "success");
    } catch (error) {
      console.error("Error opening print preview:", error);
      showMessageBox("Failed to open print preview. Please try again.", "error");
    }
  };
// ========================================
// IMPROVED LOADING DISPLAY
// Replace the loading check around line 1750 with this:
// ========================================

if (isFormLoading) {
  return (
    <div className="form-container form-loading">
      <div className="loading-spinner-container">
        <div className="loading-spinner"></div>
        <h3>🔄 Loading Invoice Details...</h3>
        <p>Please wait while we fetch the invoice data.</p>
        <div className="loading-steps">
          <div className="loading-step">⏳ Fetching invoice data...</div>
          <div className="loading-step">⏳ Loading transactions...</div>
          <div className="loading-step">⏳ Preparing form...</div>
        </div>
        <p className="loading-note">
          <strong>Note:</strong> This may take a few seconds for invoices with many transactions.
        </p>
      </div>
    </div>
  );
}
  return (
    <div className={`form-container ${submitTogether ? "combined-form" : ""}`}>
      <h2>
        {masterRecordId ? "Update " : "Create "}
        {masterFormConfig.masterFormHeading}
      </h2>
      <form
        onSubmit={submitTogether ? handleCombinedSubmit : handleMasterSubmit}
        className="form"
      >
        {Object.keys(masterFormErrors).length > 0 && (
          <p className="form-general-error">
            Please correct highlighted fields.
          </p>
        )}
        {masterRenderFields.map((fieldGroup, groupIndex) => (
          <div
            className={`form-row ${
              fieldGroup.length > 2 ? "four-column-row" : ""
            } ${fieldGroup.some(f => f.fieldName === 'wo_id') ? 'work-order-row' : ''}`}
            key={groupIndex}
          >
            {fieldGroup.map((field) => {
              const options =
                masterDropdownOptions[field.fieldName] || field.options;
              const readOnly =
  typeof field.readOnly === "function"
    ? field.readOnly(tran)
    : field.readOnly;

// 🔥 FIX: Allow remarks field to always be editable
let disabled = false;

// Skip disabled logic for remarks field
if (field.fieldName !== "remarks") {
  if (
    field.data?.dependsOnField &&
    disabledOnStatusValues === undefined
  ) {
    if (Array.isArray(field.data?.dependsOnField)) {
      disabled = !checkIfValuesExist(
        masterFormData,
        field.data.dependsOnField
      );
    } else {
      disabled = !masterFormData[field.data.dependsOnField];
    }
  } else {
    const key = masterFormData[statusField];
    if (typeof key !== "undefined") {
      for (const element of disabledOnStatusValues || []) {
        if (element[1] === "in") {
          disabled = String(key).includes(element[0]);
        } else if (element[1] === "not eq") {
          disabled = String(key) !== element[0];
        } else if (element[1] === "not in") {
          disabled = !String(key).includes(element[0]);
        }
        if (disabled === false) break;
      }
    }
  }
}
              let selectedValues = [];
              if (field.tag === "select" && field.isMulti) {
                if (Array.isArray(masterFormData[field.fieldName])) {
                  for (const o of masterFormData[field.fieldName]) {
                    const option = options?.find((opt) => {
                      if (
                        typeof o === "object" &&
                        o !== null &&
                        (o.value !== undefined || o.id !== undefined)
                      ) {
                        return opt.id === o.value || opt.value === o.label;
                      } else {
                        return field.send_value
                          ? opt.value === o
                          : opt.id === Number(o);
                      }
                    });
                    if (option !== undefined) {
                      selectedValues.push({
                        value: option.id || option.value,
                        label: option.value || option.label,
                      });
                    }
                  }
                }
              }
              return (
                <div
                  className={`form-group ${
                    masterFormErrors[field.fieldName] ? "has-error" : ""
                  } ${field.fieldName === 'wo_id' ? 'work-order-field full-width' : ''}`}
                  key={field.fieldName}
                  data-field={field.fieldName}
                >
                  {field.tag === "input" && field.inputType === "checkbox" ? (
                    <div className="checkbox-group">
                      <input
                        type="checkbox"
                        id={field.fieldName}
                        name={field.fieldName}
                        checked={masterFormData[field.fieldName] || false}
                        onChange={handleMasterChange}
                        disabled={readOnly || disabled || isLoadingMaster}
                      />
                      <label htmlFor={field.fieldName}>
                        {field.displayName}
                        {field.required && <span className="required">*</span>}
                      </label>
                    </div>
                  ) : (
                    <>
                      <label htmlFor={field.fieldName}>
                        {field.displayName}
                        {field.required && <span className="required">*</span>}
                        {field.fieldName === 'wo_id' && (
                          <span className="work-order-count">
                            ({workOrderCount} available)
                          </span>
                        )}
                      </label>
                      {field.tag === "input" && (
                        <input
                          type={field.inputType}
                          id={field.fieldName}
                          name={field.fieldName}
                          value={
                            masterFormData[field.fieldName] !== null &&
                            masterFormData[field.fieldName] !== undefined
                              ? typeof masterFormData[field.fieldName] ===
                                "object"
                                ? masterFormData[field.fieldName].id
                                : masterFormData[field.fieldName]
                              : ""
                          }
                          onChange={handleMasterChange}
                          disabled={readOnly || disabled || isLoadingMaster}
                          placeholder={field.placeholder}
                        />
                      )}
                      {field.tag === "select" && field.isMulti ? (
                        <MultiSelect
                          options={
                            options
                              ?.filter((o) => o != null)
                              .map((opt) => ({
                                value: opt.id || opt.value,
                                label: opt.value || opt.label,
                              })) || []
                          }
                          selectedValues={selectedValues}
                          onSelect={(selectedOpts) =>
                            handleMasterChange(field.fieldName, selectedOpts)
                          }
                          placeholder={`Select ${field.displayName}`}
                          disabled={readOnly || disabled || isLoadingMaster}
                        />
                      ) : (
                        field.tag === "select" && (
                          <select
                            id={field.fieldName}
                            name={field.fieldName}
                            value={
                              masterFormData[field.fieldName]?.id ||
                              masterFormData[field.fieldName] ||
                              ""
                            }
                            onChange={handleMasterChange}
                            disabled={readOnly || disabled || isLoadingMaster}
                            required={field.required}
                          >
                            <option value="">Select {field.displayName}</option>
                            {options?.map((option) => (
                              <option
                                key={option.id || option.value}
                                value={option.id || option.value}
                              >
                                {option.value || option.label}
                              </option>
                            ))}
                          </select>
                        )
                      )}
                      {field.tag === "textarea" && (
                        <textarea
                          id={field.fieldName}
                          name={field.fieldName}
                          value={masterFormData[field.fieldName] || ""}
                          onChange={handleMasterChange}
                          disabled={readOnly || disabled || isLoadingMaster}
                          rows={field.rows || 1}
                        ></textarea>
                      )}
                      {field.tag === "combobox" && (
                        <Combobox
                          options={masterDropdownOptions[field.fieldName] || []}
                          value={masterFormData[field.fieldName]}
                          onChange={(selectedObject) =>
                            handleMasterChange({
                              target: {
                                name: field.fieldName,
                                value: selectedObject,
                              },
                            })
                          }
                          placeholder={`Select ${field.displayName}`}
                          disabled={readOnly || disabled || isLoadingMaster}
                          getOptionLabel={field.getOptionLabel}
                          getOptionValue={field.getOptionValue}
                          isOptionDisabled={field.isOptionDisabled}
                          renderOption={field.renderOption}
                        />
                      )}
                      {field.tag === "list" && (
                        <div className="list-display">
                          {masterListData[field.fieldName] &&
                          masterListData[field.fieldName].length > 0 ? (
                            <table className="list-table">
                              <thead>
                                <tr>
                                  {field.columns.map((f, i) => (
                                    <th key={i}>{f.headName}</th>
                                  ))}
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {masterListData[field.fieldName].map((item) => {
                                  const idFieldName = field.identifier;
                                  const transactionIdentifierField =
                                    transactionFormConfig.formFields.find(
                                      (f) => f.identifier
                                    )?.fieldName;
                                  const isItemAdded = transactionIdentifierField
                                    ? transactions.some((t) => {
                                        const transactionItemValue =
                                          t[transactionIdentifierField];
                                        if (
                                          typeof transactionItemValue ===
                                            "object" &&
                                          transactionItemValue !== null
                                        ) {
                                          return (
                                            transactionItemValue.id ===
                                            item[idFieldName]
                                          );
                                        }
                                        return (
                                          transactionItemValue ===
                                          item[idFieldName]
                                        );
                                      })
                                    : false;
                                  return (
                                    <tr key={item[idFieldName]}>
                                      {field.columns.map((f, i) => (
  <td key={i}>
    {Array.isArray(f.fieldName)
      ? f.fieldName
          .map((e) => item[e])
          .filter(
            (e) =>
              e !== null &&
              e !== undefined &&
              e !== '' // Added: filter empty strings
          )
          .join(' | ')
      : f.fieldName.includes("date") && item[f.fieldName]
      ? formatDateForTable(item[f.fieldName])
      : item[f.fieldName] || 'N/A'} {/* Added: fallback to 'N/A' */}
  </td>
))}
                                      <td>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            addItemToTransaction(item)
                                          }
                                          className="add-to-transactions-button"
                                          disabled={
                                            isLoadingTransactions || isItemAdded
                                          }
                                        >
                                          {isItemAdded
                                            ? "Added"
                                            : "Add to Transactions"}
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          ) : (
                            <p className="no-list-data-message">
                              No assets found for the selected Work Order.
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {masterFormErrors[field.fieldName] && (
                    <p className="error-message">
                      {masterFormErrors[field.fieldName]}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        ))}
        <div className="form-group grid-column-span-3">
          {masterRecordId && (
            <div className="attachment-section">
              <h3 className="attachment-section-title">
                Manage Existing Attachments
              </h3>
              {existingAttachments.length > 0 ? (
                <div className="attachments-table-container">
                  <table className="attachments-table">
                    <thead>
                      <tr>
                        <th>File Name</th>
                        <th>Description</th>
                        <th>Added By</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {existingAttachments.map((att) => (
                        <tr key={att.id}>
                          <td>
                            <a
                              href={att.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="attachment-link"
                            >
                              <IoAttach /> {getFileNameFromUrl(att.file_url)}
                            </a>
                          </td>
                          <td>{att.description || ""}</td>
                          <td>
                            {userMap[att.added_by] || `ID: ${att.added_by}`}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="remove-file-btn"
                              onClick={() =>
                                handleDeleteExistingAttachment(att.id)
                              }
                              title="Remove attachment"
                              disabled={isLoadingMaster}
                            >
                              <IoTrashOutline />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="no-attachments-message">
                  No existing attachments.
                </p>
              )}
            </div>
          )}
          <div className="attachment-section">
            <h3 className="attachment-section-title">
              {masterRecordId ? "Add New Attachments" : "Attachments"}
            </h3>
            <label
              htmlFor="image_upload"
              className={`drop-zone ${isDragging ? "drag-over" : ""}`}
              onDrop={handleFileDrop}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
            >
              <input
                id="image_upload"
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx"
                onChange={handleFileChange}
                className="file-input-hidden"
                disabled={isLoadingMaster}
              />
              <div className="drop-zone-content">
                <IoCloudUploadOutline />{" "}
                <span>
                  {isDragging
                    ? "Drop files here"
                    : "Drag & Drop or Click to Upload"}
                </span>
              </div>
            </label>
            {selectedFiles.length > 0 && (
              <div className="file-description-list">
                <h4 className="file-list-header">New Files to Upload</h4>
                {selectedFiles.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="file-description-item"
                  >
                    <span className="file-name-label" title={file.name}>
                      {file.name}
                    </span>
                    <input
                      type="text"
                      value={fileDescriptions[index]}
                      onChange={(e) =>
                        handleDescriptionChange(index, e.target.value)
                      }
                      placeholder="Enter a brief description..."
                      className="file-description-input"
                      disabled={isLoadingMaster}
                    />
                    <button
                      type="button"
                      className="remove-file-btn"
                      onClick={() => handleRemoveNewFile(index)}
                      title="Remove file"
                      disabled={isLoadingMaster}
                    >
                      <IoTrashOutline />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        {submitTogether && (
          <TransactionSection
            masterFormConfig={masterFormConfig}
            transactionFormConfig={transactionFormConfig}
            masterRecordId={masterRecordId}
            masterFormData={masterFormData}
            transactions={transactions}
            transactionErrors={transactionErrors}
            isLoadingTransactions={isLoadingTransactions}
            transactionDropdownOptions={transactionDropdownOptions}
            transactionComboboxOptions={transactionComboboxOptions}
            submitTogether={submitTogether}
            addTransaction={addTransaction}
            duplicateTransaction={duplicateAssetTransaction}
            handleTransactionChange={handleTransactionChange}
            removeTransaction={removeTransaction}
            saveAllTransactions={saveAllTransactions}
            transactionSectionLoads={transactionSectionLoads}
            summaryFields={transactionSummaryFields}
            calculateTransactionTotals={calculateTransactionTotals}
            getSummaryColSpan={getSummaryColSpan}
            disabledOnStatusValues={disabledOnStatusValues}
            statusField={statusField}
          />
        )}
{/* ========================================
    CHECKER SELECTION - Show for Draft/Rejected
    ======================================== */}
{shouldShowCheckerSelection() && masterRecordId && (
  <div className="form-group grid-column-span-3">
    <SelectChecker
      moduleName={moduleName}
      currentStatus={currentStatus}
      handleChange={setCheckers}
      creatorId={masterFormData.added_by}
    />
  </div>
)}

{/* ========================================
    APPROVER SELECTION - Show ONLY when CHECKER needs to select approvers
    ======================================== */}
{shouldShowApproverSelection() && checkPermit && !approvePermit && (
  <div className="form-group grid-column-span-3">
    <SelectApprover
      moduleName={moduleName}
      currentStatus={currentStatus}
      handleChange={setApprovers}
    />
    {/* ✅ Show selected count */}
    {approvers.length > 0 && (
      <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#e8f5e9', borderRadius: '4px' }}>
        <p style={{ margin: 0, fontSize: '13px', color: '#2e7d32' }}>
          ✓ {approvers.length} approver{approvers.length > 1 ? 's' : ''} selected
        </p>
      </div>
    )}
  </div>
)}

{/* ========================================
    STATUS VIEW
    ======================================== */}
{statuses && (
  <StatusView
    currentStatus={currentStatus}
    masterFormData={masterFormData}
    statusField={statusField}
  />
)}

{/* ========================================
    PRINT BUTTON
    ======================================== */}
<div className="form-row inline">
  {isPrintEnabled() && (
    <button
      type="button"
      className="submit-button print-button"
      onClick={handlePrintInvoice}
      disabled={isLoadingMaster}
    >
      <IoPrintOutline /> Print Invoice
    </button>
  )}
  
  {/* Regular status buttons (Send for Checking) */}
  {statuses && statuses.map((status) => {
    if (status.buttonLabel === "Check Actions" || status.buttonLabel === "Approval Actions") {
      return null;
    }
    
    const isSendForChecking = status.buttonLabel === "Send for Checking";
    const currentStatusValue = masterFormData[statusField];
    
    // ✅ Show "Send for Checking" for Draft, Rejected by Checker, AND Rejected by Approver
    const shouldShowSendButton = isSendForChecking && (
      currentStatusValue === "Drafted" || 
      currentStatusValue === "Rejected by Checker" ||
      currentStatusValue === "Rejected by Approver"
    );
    
    if (isSendForChecking) {
      return shouldShowSendButton ? (
        <button
          type="button"
          className="submit-button stages-button"
          key={status.id}
          disabled={!masterRecordId || checkers.length === 0 || isLoadingMaster}
          onClick={() => handleSendForChecking(status)}
        >
          {isLoadingMaster ? "Sending..." : "Send for Checking"}
        </button>
      ) : null;
    }
    
    return (
      <button
        type="submit"
        className="submit-button stages-button"
        key={status.id}
        disabled={
          Array.isArray(status.enabledOnStatus)
            ? !status.enabledOnStatus.includes(masterFormData[statusField])
            : masterFormData[statusField] !== status.enabledOnStatus
        }
        onClick={async (e) => {
          e.preventDefault();
          if (status.submit) {
            setIsLoadingMaster(true);
            await status.submit(
              masterRecordId,
              () => handleCombinedSubmit({ preventDefault: () => {} }),
              approvers,
              navigate
            );
            setIsLoadingMaster(false);
          }
        }}
      >
        {isLoadingMaster ? "Processing..." : status.buttonLabel}
      </button>
    );
  })}
</div>

{/* ========================================
    ✅ CHECKER ACTIONS - Show when "Waiting for check"
    ======================================== */}
{masterRecordId && masterFormData[statusField] === "Waiting for check" && checkPermit && (
  <div className="form-row inline" style={{ marginTop: '10px', gap: '10px', borderTop: '2px solid #ddd', paddingTop: '15px' }}>
    
    {/* ✅ Show permission status */}
    <div style={{ width: '100%', padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '4px', marginBottom: '10px' }}>
      <p style={{ margin: '5px 0 0 0', fontSize: '13px' }}>
        Selected approvers: {approvers.length > 0 ? approvers.length : 'None'}
      </p>
    </div>
    
    {/* ✅ APPROVE BUTTON - Requires approver selection */}
    <button
      type="button"
      className="submit-button"
      disabled={!masterRecordId || approvers.length === 0 || isLoadingMaster}
      onClick={() => handleCheckerApproveForApproval({
        id: "check-invoice",
        permissionName: "CHECK"
      })}
    >
      {isLoadingMaster ? "Processing..." : "Checked & Send for Approval"}
    </button>
    
    {/* ✅ REJECT BUTTON with Modal */}
    <Modal
      buttonLabel={isLoadingMaster ? "Please wait..." : "Reject Invoice"}
      buttonClassName="submit-button"
      buttonDisabled={isLoadingMaster}
      children={
        <div className="form-group">
          <label htmlFor="check_remark">
            Rejection Reason <span style={{ color: 'red' }}>*</span>
          </label>
          <textarea 
            id="check_remark" 
            name="check_remark" 
            rows={4}
            placeholder="Please provide a detailed reason for rejection..."
            required
            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
          ></textarea>
        </div>
      }
      successFunction={async (e) => {
        e.preventDefault();
        const remark = document.getElementById("check_remark");
        if (!remark.value.trim()) {
          showMessageBox("Please provide a rejection reason.", "error");
          return;
        }
        
        setIsLoadingMaster(true);
        
        const token = localStorage.getItem("zTaskAuthToken");
        try {
          const response = await fetch(
            `${ATTACHMENT_API_URL}/invoice/api/invoices/status/${masterRecordId}`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                stage: "Check Invoice",
                to: "rejected",
                remarks: remark.value,
              }),
            }
          );
          
          if (response.ok) {
            showMessageBox("Invoice rejected by checker.", "success");
            setTimeout(() => navigate('/invoices'), 1500);
          } else {
            const error = await response.json();
            showMessageBox(error.error || "Failed to reject invoice.", "error");
          }
        } catch (error) {
          console.error('Error rejecting invoice:', error);
          showMessageBox("Network error. Please try again.", "error");
        } finally {
          setIsLoadingMaster(false);
        }
      }}
    />
  </div>
)}

{/* ========================================
    ✅ APPROVER ACTIONS - Show when "Waiting for approval"
    ======================================== */}
{shouldShowApproverActions() && (
  <div className="form-row inline" style={{ marginTop: '10px', gap: '10px', borderTop: '2px solid #ddd', paddingTop: '15px' }}>
    
    {/* ✅ APPROVE BUTTON */}
    <button
      type="button"
      className="submit-button"
      disabled={!masterRecordId || isLoadingMaster}
      onClick={handleApproverApprove}
    >
      {isLoadingMaster ? "Processing..." : "✓ Approve Invoice"}
    </button>
    
    {/* ✅ REJECT BUTTON with Modal */}
    <Modal
      buttonLabel={isLoadingMaster ? "Please wait..." : "✗ Reject Invoice"}
      buttonClassName="submit-button"
      buttonDisabled={isLoadingMaster}
      children={
        <div className="form-group">
          <label htmlFor="approval_remark">
            Rejection Reason <span style={{ color: 'red' }}>*</span>
          </label>
          <textarea 
            id="approval_remark" 
            name="approval_remark" 
            rows={4}
            placeholder="Please provide a detailed reason for rejection..."
            required
            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
          ></textarea>
        </div>
      }
      successFunction={async (e) => {
        e.preventDefault();
        const remark = document.getElementById("approval_remark");
        if (!remark.value.trim()) {
          showMessageBox("Please provide a rejection reason.", "error");
          return;
        }
        
        setIsLoadingMaster(true);
        
        const token = localStorage.getItem("zTaskAuthToken");
        try {
          const response = await fetch(
            `${ATTACHMENT_API_URL}/invoice/api/invoices/status/${masterRecordId}`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                stage: "Approval",
                to: "rejected",
                remarks: remark.value,
              }),
            }
          );
          
          if (response.ok) {
            showMessageBox("Invoice rejected by approver.", "success");
            setTimeout(() => navigate('/invoices'), 1500);
          } else {
            const error = await response.json();
            showMessageBox(error.error || "Failed to reject invoice.", "error");
          }
        } catch (error) {
          console.error('Error rejecting invoice:', error);
          showMessageBox("Network error. Please try again.", "error");
        } finally {
          setIsLoadingMaster(false);
        }
      }}
    />
  </div>
)}

        

      </form>
      {!submitTogether && (
        <TransactionSection
          masterFormConfig={masterFormConfig}
          transactionFormConfig={transactionFormConfig}
          masterRecordId={masterRecordId}
          masterFormData={masterFormData}
          transactions={transactions}
          transactionErrors={transactionErrors}
          isLoadingTransactions={isLoadingTransactions}
          transactionDropdownOptions={transactionDropdownOptions}
          transactionComboboxOptions={transactionComboboxOptions}
          submitTogether={submitTogether}
          addTransaction={addTransaction}
          duplicateTransaction={duplicateAssetTransaction}
          handleTransactionChange={handleTransactionChange}
          removeTransaction={removeTransaction}
          saveAllTransactions={saveAllTransactions}
          transactionSectionLoads={transactionSectionLoads}
          summaryFields={transactionSummaryFields}
          calculateTransactionTotals={calculateTransactionTotals}
          getSummaryColSpan={getSummaryColSpan}
          statusField={statusField}
        />
      )}
    </div>
  );
};

// ========================================
// ✅ FIXED: Checker/Approver Selection Components
// ========================================

const SelectChecker = ({ moduleName, currentStatus, handleChange, creatorId }) => {
  const [users, setUsers] = useState([]);
  const [selectedChecker, setSelectedChecker] = useState(null);
  const [dataLoadError, setDataLoadError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // ✅ FIXED: Use exact permission title
  const queryObj = {
    tables: ["users", "role_permissions", "permissions"],
    joins: [
      `permissions.title = 'CHECK INVOICE'`,
      "role_permissions.permission_id = permissions.permission_id",
      "users.role_id = role_permissions.role_id",
      "users.active = true"
    ],
  };

  const queryString = useMemo(() => objectToQueryString(queryObj), [moduleName]);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/api/get-column/dynamic/users/name?${queryString}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("zTaskAuthToken")}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch checkers");
      const data = await response.json();
      
      const filteredUsers = data.filter(user => user.id !== creatorId);
      console.log("✅ Fetched checkers:", filteredUsers);
      
      setUsers(filteredUsers);
      setDataLoadError(null);
    } catch (error) {
      console.error("❌ Error fetching checkers:", error);
      setDataLoadError("Failed to load checkers.");
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, [queryString, creatorId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCheckerChange = (e) => {
    const selectedId = e.target.value;
    if (!selectedId) {
      setSelectedChecker(null);
      if (handleChange) handleChange([]);
      return;
    }

    const selected = users.find((u) => String(u.id) === selectedId);
    if (selected) {
      setSelectedChecker(selected);
      if (handleChange) handleChange([parseInt(selected.id, 10)]);
    }
  };

  return (
    <div className="checker-selection-section">
      <h4>Select Checker <span style={{ color: "#dc3545" }}>*</span></h4>
      <p style={{ fontSize: "13px", color: "#6c757d", marginBottom: "10px" }}>
        Select the person who will check this invoice before approval
      </p>

      {isLoading ? (
        <p>Loading checkers...</p>
      ) : dataLoadError ? (
        <div>
          ⚠️ {dataLoadError}
          <button onClick={fetchUsers}>Retry</button>
        </div>
      ) : users.length === 0 ? (
        <div>⚠️ No checkers found with required permissions.</div>
      ) : (
        <>
          <select
            value={selectedChecker?.id || ""}
            onChange={handleCheckerChange}
            style={{ width: "100%", padding: "10px" }}
          >
            <option value="">-- Select Checker --</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.value || user.name}
              </option>
            ))}
          </select>

          {selectedChecker && (
            <div style={{ marginTop: "10px", color: "#155724" }}>
              ✓ Selected: <strong>{selectedChecker.value || selectedChecker.name}</strong>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const SelectApprover = ({ moduleName, currentStatus, handleChange }) => {
  const [users, setUsers] = useState([]);
  const [selectedApprover, setSelectedApprover] = useState(null);
  const [dataLoadError, setDataLoadError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // ✅ FIXED: Use exact permission title
  const queryObj = {
    tables: ["users", "role_permissions", "permissions"],
    joins: [
      `permissions.title = 'APPROVE INVOICE'`,
      "role_permissions.permission_id = permissions.permission_id",
      "users.role_id = role_permissions.role_id",
      "users.active = true"
    ],
  };

  const queryString = useMemo(() => objectToQueryString(queryObj), [moduleName]);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/api/get-column/dynamic/users/name?${queryString}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("zTaskAuthToken")}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch approvers");
      const data = await response.json();
      console.log("✅ Fetched approvers:", data);
      setUsers(data);
      setDataLoadError(null);
    } catch (error) {
      console.error("❌ Error fetching approvers:", error);
      setDataLoadError("Failed to load approvers.");
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, [queryString]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleApproverChange = (e) => {
    const selectedId = e.target.value;
    if (!selectedId) {
      setSelectedApprover(null);
      if (handleChange) handleChange([]);
      return;
    }

    const selected = users.find((u) => String(u.id) === selectedId);
    if (selected) {
      setSelectedApprover(selected);
      if (handleChange) handleChange([parseInt(selected.id, 10)]);
    }
  };

  return (
    <div className="approver-selection-section">
      <h4>Select Approver <span style={{ color: "#dc3545" }}>*</span></h4>
      <p style={{ fontSize: "13px", color: "#6c757d", marginBottom: "10px" }}>
        Select the person who will approve this invoice
      </p>

      {isLoading ? (
        <p>Loading approvers...</p>
      ) : dataLoadError ? (
        <div>
          ⚠️ {dataLoadError}
          <button onClick={fetchUsers}>Retry</button>
        </div>
      ) : users.length === 0 ? (
        <div>⚠️ No approvers found with required permissions.</div>
      ) : (
        <>
          <select
            value={selectedApprover?.id || ""}
            onChange={handleApproverChange}
            style={{ width: "100%", padding: "10px" }}
          >
            <option value="">-- Select Approver --</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.value || user.name}
              </option>
            ))}
          </select>

          {selectedApprover && (
            <div style={{ marginTop: "10px", color: "#155724" }}>
              ✓ Selected: <strong>{selectedApprover.value || selectedApprover.name}</strong>
            </div>
          )}
        </>
      )}
    </div>
  );
};


const TransactionSection = ({
  masterFormConfig,
  transactionFormConfig,
  masterRecordId,
  masterFormData,
  transactions,
  transactionErrors,
  isLoadingTransactions,
  transactionDropdownOptions,
  transactionComboboxOptions,
  submitTogether,
  addTransaction,
  duplicateTransaction,
  handleTransactionChange,
  removeTransaction,
  saveAllTransactions,
  transactionSectionLoads,
  summaryFields,
  calculateTransactionTotals,
  getSummaryColSpan,
  disabledOnStatusValues,
  statusField,
}) => {
  const [areLoadsComplete, setAreLoadsComplete] = useState(false);
  const [openRateHelp, setOpenRateHelp] = useState(null);
  const identifierField = useMemo(
    () => transactionFormConfig.formFields.find((f) => f.identifier),
    [transactionFormConfig.formFields]
  );
  useEffect(() => {
    const loadDependencies = async () => {
      if (typeof transactionSectionLoads === "undefined") {
        setAreLoadsComplete(true);
        return;
      }
      const loadPromises = transactionSectionLoads.map(async (load) => {
        await load.loadFunction();
      });
      await Promise.all(loadPromises);
      setAreLoadsComplete(true);
    };
    loadDependencies();
  }, [transactionSectionLoads]);
  const totals = useMemo(() => {
    return calculateTransactionTotals(transactions, summaryFields);
  }, [transactions, calculateTransactionTotals, summaryFields]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openRateHelp !== null) {
        const rateHelpMenu = document.querySelector(
          `[data-rate-help-index="${openRateHelp}"]`
        );
        const toggleButton = document.querySelector(
          `[data-rate-help-toggle="${openRateHelp}"]`
        );
     
        if (
          (rateHelpMenu && !rateHelpMenu.contains(event.target)) &&
          (toggleButton && !toggleButton.contains(event.target))
        ) {
          setOpenRateHelp(null);
        }
      }
    };
    if (openRateHelp !== null) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [openRateHelp]);
  const handleSelectRateFromHelp = async (index, rateKey) => {
    const transaction = transactions[index];
    if (!identifierField) {
      console.error("Rate help requires identifier field");
      return;
    }
    const assetData = transaction[identifierField.fieldName]?.original;
    if (!assetData || !assetData[rateKey]) {
      showMessageBox("Rate not available for this asset.", "warning");
      return;
    }
    const newRate = parseFloat(assetData[rateKey]);
 
    setOpenRateHelp(null);
 
    setTimeout(() => {
      const syntheticEvent = {
        target: {
          name: "rate",
          value: newRate.toString(),
          type: "number",
        },
      };
   
      handleTransactionChange(index, syntheticEvent);
    }, 50);
  };
  if (!areLoadsComplete) return null;
  return (
    <div className="transaction-section">
      <h3>
        Transactions for {masterFormConfig.masterFormHeading} ID:{" "}
        {masterRecordId || "N/A (Will be assigned on submit)"}
      </h3>
      <p className="master-dates-info">
        Master Period:{" "}
        {(masterFormData.start_date || masterFormData.start_date)
          ?.toLocaleString() || "N/A"}{" "}
        to{" "}
        {(masterFormData.end_date || masterFormData.end_date)
          ?.toLocaleString() || "N/A"}
      </p>
      <div className="transaction-table-container">
        <table>
          <thead>
            <tr>
              {transactionFormConfig.formFields.map((field) => (
                <th key={field.fieldName} style={field.properties}>
                  {field.displayName}
                  {field.required && <span className="required">*</span>}
                </th>
              ))}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {transactions.length === 0 && (
              <tr>
                <td
                  colSpan={transactionFormConfig.formFields.length + 1}
                  style={{ textAlign: "center" }}
                >
                  No transactions added yet.
                </td>
              </tr>
            )}
            {transactions.map((tran, index) => {
              const currentErrors = transactionErrors[index] || {};
              const hasDateError =
                currentErrors.start_date || currentErrors.end_date;
              const assetData = identifierField && tran[identifierField.fieldName]
                ? (tran[identifierField.fieldName].original || tran[identifierField.fieldName])
                : null;
              console.log('DEBUG: Rendering row', index, 'assetData:', assetData);
              return (
                <tr
                  key={tran.id}
                  className={
                    Object.keys(currentErrors).length > 0 ? "has-error" : ""
                  }
                >
                  {transactionFormConfig.formFields.map((field) => {
                    let value = "";
                    if (
                      tran[field.fieldName] !== null &&
                      tran[field.fieldName] !== undefined
                    ) {
                      if (typeof tran[field.fieldName] === "function") {
                        value = getValueWithType(
                          field,
                          tran[field.fieldName](masterFormData, transactions)
                        );
                      } else {
                        value = getValueWithType(field, tran[field.fieldName]);
                      }
                    } else {
                      value = getValueWithType(
                        field,
                        tran.defaultValues?.[field.fieldName]
                      );
                      if (tran[field.fieldName] === undefined) {
                        tran[field.fieldName] = value;
                      }
                    }
                    const readOnly =
                      typeof field.readOnly === "function"
                        ? field.readOnly(tran)
                        : field.readOnly;
                    let disabled = false;
                    if (
                      field.data?.dependsOnField &&
                      disabledOnStatusValues === undefined
                    ) {
                      if (Array.isArray(field.data?.dependsOnField)) {
                        disabled = !checkIfValuesExist(
                          masterFormData,
                          field.data.dependsOnField
                        );
                      } else {
                        disabled = !masterFormData[field.data.dependsOnField];
                      }
                    } else {
                      const key = masterFormData[statusField];
                      if (typeof key !== "undefined") {
                        for (const element of disabledOnStatusValues || []) {
                          if (element[1] === "in") {
                            disabled = String(key).includes(element[0]);
                          } else if (element[1] === "not eq") {
                            disabled = String(key) !== element[0];
                          } else if (element[1] === "not in") {
                            disabled = !String(key).includes(element[0]);
                          }
                          if (disabled === false) break;
                        }
                      }
                    }
                    return (
                      <td
                        key={field.fieldName}
                        style={field.properties}
                        className={
                          field.fieldName === "rate"
                            ? "rate-cell-container"
                            : ""
                        }
                      >
                        {field.identifier && readOnly ? (
                          <span>{tran[field.fieldName]?.label}</span>
                        ) : field.tag === "span" ? (
                          <span>{tran[field.fieldName]}</span>
                        ) : field.hasHelpButton && field.fieldName === "rate" ? (
                          <div className="rate-cell-wrapper">
                            <input
                              type="number"
                              name={field.fieldName}
                              value={value}
                              onChange={(e) => handleTransactionChange(index, e)}
                              placeholder={field.placeholder}
                              disabled={disabled || readOnly}
                              className={
                                currentErrors[field.fieldName] ? "input-error" : ""
                              }
                              step="0.01"
                            />
                            <div className="rate-help-container">
                              <button
                                type="button"
                                className="rate-help-toggle"
                                data-rate-help-toggle={index}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setOpenRateHelp(openRateHelp === index ? null : index);
                                  console.log('DEBUG: Toggle clicked for index:', index, 'openRateHelp now:', openRateHelp === index ? null : index);
                                }}
                                disabled={disabled || readOnly || !assetData}
                                title="Show available rates"
                              >
                                ?
                              </button>
                              {openRateHelp === index && assetData && (
                                <div
                                  className="rate-help-menu"
                                  data-rate-help-index={index}
                                  role="menu"
                                >
                                  <div className="rate-options-list">
                                    <button
  type="button"
  role="menuitem"
  onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('DEBUG: Hourly rate button clicked for index:', index);
    handleSelectRateFromHelp(index, "rate_per_hr").catch(err =>
      console.error('Error selecting hourly rate:', err)
    );
  }}
  disabled={
    !assetData || 
    assetData.rate_per_hr == null ||  // ✅ FIXED: Use loose equality
    assetData.rate_per_hr === '' ||
    (typeof assetData.rate_per_hr === 'number' && isNaN(assetData.rate_per_hr))
  }
  className="rate-option-button"
>
  <span className="rate-label">Hourly Rate</span>
  <span className="rate-value">
    {assetData?.rate_per_hr != null  // ✅ FIXED: Use loose equality
      ? Number(assetData.rate_per_hr).toFixed(2)
      : "N/A"}
  </span>
</button>
                                    <button
                                      type="button"
                                      role="menuitem"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('DEBUG: Daily rate button clicked for index:', index);
                                        handleSelectRateFromHelp(index, "daily_rate").catch(err =>
                                          console.error('Error selecting daily rate:', err)
                                        );
                                      }}
                                      disabled={!assetData || typeof assetData.daily_rate !== "number"}
                                      className="rate-option-button"
                                    >
                                      <span className="rate-label">Daily Rate</span>
                                      <span className="rate-value">
                                        {assetData?.daily_rate != null
                                          ? Number(assetData.daily_rate).toFixed(2)
                                          : "N/A"}
                                      </span>
                                    </button>
                                    <button
                                      type="button"
                                      role="menuitem"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('DEBUG: Monthly rate button clicked for index:', index);
                                        handleSelectRateFromHelp(index, "monthly_rate").catch(err =>
                                          console.error('Error selecting monthly rate:', err)
                                        );
                                      }}
                                      disabled={!assetData || typeof assetData.monthly_rate !== "number"}
                                      className="rate-option-button"
                                    >
                                      <span className="rate-label">Monthly Rate</span>
                                      <span className="rate-value">
                                        {assetData?.monthly_rate != null
                                          ? Number(assetData.monthly_rate).toFixed(2)
                                          : "N/A"}
                                      </span>
                                    </button>
                                    <button
                                      type="button"
                                      role="menuitem"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('DEBUG: Mob rate button clicked for index:', index);
                                        handleSelectRateFromHelp(index, "mob").catch(err =>
                                          console.error('Error selecting mob rate:', err)
                                        );
                                      }}
                                      disabled={!assetData || typeof assetData.mob !== "number"}
                                      className="rate-option-button"
                                    >
                                      <span className="rate-label">Mob</span>
                                      <span className="rate-value">
                                        {assetData?.mob != null
                                          ? Number(assetData.mob).toFixed(2)
                                          : "N/A"}
                                      </span>
                                    </button>
                                    <button
                                      type="button"
                                      role="menuitem"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('DEBUG: Demob rate button clicked for index:', index);
                                        handleSelectRateFromHelp(index, "demob").catch(err =>
                                          console.error('Error selecting demob rate:', err)
                                        );
                                      }}
                                      disabled={!assetData || typeof assetData.demob !== "number"}
                                      className="rate-option-button"
                                    >
                                      <span className="rate-label">Demob</span>
                                      <span className="rate-value">
                                        {assetData?.demob != null
                                          ? Number(assetData.demob).toFixed(2)
                                          : "N/A"}
                                      </span>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                            {currentErrors[field.fieldName] && (
                              <p className="error-message-inline">
                                {currentErrors[field.fieldName]}
                              </p>
                            )}
                          </div>
                        ) : field.tag === "input" && field.inputType !== "checkbox" ? (
                          <>
                            <input
                              type={field.inputType}
                              name={field.fieldName}
                              value={value}
                              onChange={(e) => handleTransactionChange(index, e)}
                              placeholder={field.placeholder}
                              disabled={disabled || readOnly}
                              className={
                                currentErrors[field.fieldName] ? "input-error" : ""
                              }
                             {...(field.fieldName === "quantity" && field.inputType === "number" ? { step: "0.01" } : field.inputType === "number" ? { min: "0", step: "0.01" } : {})}
                            />
                            {currentErrors[field.fieldName] && (
                              <p className="error-message-inline">
                                {currentErrors[field.fieldName]}
                              </p>
                            )}
                          </>
                        ) : field.tag === "input" && field.inputType === "checkbox" ? (
                          <>
                            <input
                              type="checkbox"
                              name={field.fieldName}
                              checked={tran[field.fieldName] || false}
                              onChange={(e) => handleTransactionChange(index, e)}
                              disabled={disabled || readOnly}
                              className={
                                currentErrors[field.fieldName] ? "input-error" : ""
                              }
                            />
                            {currentErrors[field.fieldName] && (
                              <p className="error-message-inline">
                                {currentErrors[field.fieldName]}
                              </p>
                            )}
                          </>
                        ) : field.tag === "select" ? (
                          <>
                            <select
                              name={field.fieldName}
                              value={
                                tran[field.fieldName]?.id ||
                                tran[field.fieldName] ||
                                ""
                              }
                              onChange={(e) => handleTransactionChange(index, e)}
                              className={
                                currentErrors[field.fieldName]
                                  ? "input-error"
                                  : ""
                              }
                              disabled={disabled || readOnly}
                            >
                              <option value="">Select {field.displayName}</option>
                              {(
                                field.options ||
                                transactionDropdownOptions[index]?.[
                                  field.fieldName
                                ]
                              )?.map((option) => (
                                <option
                                  key={option.id || option.value}
                                  value={option.id || option.value}
                                >
                                  {option.label || option.value}
                                </option>
                              ))}
                            </select>
                            {currentErrors[field.fieldName] && (
                              <p className="error-message-inline">
                                {currentErrors[field.fieldName]}
                              </p>
                            )}
                          </>
                        ) : field.tag === "combobox" ? (
                          <>
                            <Combobox
                              options={
                                transactionComboboxOptions[index]?.[
                                  field.fieldName
                                ] || []
                              }
                              value={tran[field.fieldName]}
                              onChange={(selectedId) =>
                                handleTransactionChange(index, {
                                  target: {
                                    name: field.fieldName,
                                    value: selectedId,
                                  },
                                })
                              }
                              placeholder={
                                hasDateError
                                  ? "Set dates first"
                                  : `Select ${field.displayName}`
                              }
                              disabled={disabled || readOnly}
                              className={
                                currentErrors[field.fieldName]
                                  ? "input-error"
                                  : ""
                              }
                              getOptionLabel={field.getOptionLabel}
                              getOptionValue={field.getOptionValue}
                              isOptionDisabled={(option) =>
                                field.isOptionDisabled
                                  ? field.isOptionDisabled(option, masterRecordId)
                                  : false
                              }
                              renderOption={field.renderOption}
                            />
                            {currentErrors[field.fieldName] && (
                              <p className="error-message-inline">
                                {currentErrors[field.fieldName]}
                              </p>
                            )}
                          </>
                        ) : field.tag === "textarea" ? (
                          <>
                            <textarea
                              id={field.fieldName}
                              name={field.fieldName}
                              value={value}
                              onChange={(e) => handleTransactionChange(index, e)}
                              disabled={disabled || readOnly}
                              rows={field.rows || 1}
                              className={
                                currentErrors[field.fieldName]
                                  ? "input-error"
                                  : ""
                              }
                            ></textarea>
                            {currentErrors[field.fieldName] && (
                              <p className="error-message-inline">
                                {currentErrors[field.fieldName]}
                              </p>
                            )}
                          </>
                        ) : (
                          <>
                            <span>{tran[field.fieldName] || "N/A"}</span>
                            {currentErrors[field.fieldName] && (
                              <p className="error-message-inline">
                                {currentErrors[field.fieldName]}
                              </p>
                            )}
                          </>
                        )}
                      </td>
                    );
                  })}
                  {/* ========================================
                  2. UPDATE THE TransactionSection COMPONENT
                  In the Actions column (<td className="transaction-row-actions">)
                  Replace the existing buttons with this:
                  ======================================== */}
                  <td className="transaction-row-actions">
                    <button
                      type="button"
                      onClick={() => duplicateTransaction(index)}
                      className="add-transaction-button"
                      title="Add another transaction for this asset"
                      disabled={isLoadingTransactions || !tran.asset_id}
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => removeTransaction(index, tran.dbId)}
                      className="remove-transaction-button"
                      title="Remove this row"
                    >
                      <IoTrashOutline /> Remove
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {transactions.length > 0 && (
            <tfoot>
              <tr>
                <td
                  colSpan={getSummaryColSpan(
                    transactionFormConfig,
                    summaryFields
                  )}
                  className="total-label"
                >
                  Total
                </td>
                {transactionFormConfig.formFields
                  .filter((f) => summaryFields.includes(f.fieldName))
                  .map((field) => (
                    <td
                      key={`total-${field.fieldName}`}
                      className="total-value"
                    >
                      {parseFloat(totals[field.fieldName] || 0).toFixed(2)}
                    </td>
                  ))}
                <td className="total-actions-cell"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <div className="transaction-actions">
  {!submitTogether && (
    <button
      type="button"
      onClick={() => saveAllTransactions(masterRecordId)}
      className="submit-button"
      disabled={isLoadingTransactions || transactions.length === 0}
    >
      {isLoadingTransactions
        ? "Saving Transactions..."
        : "Save All Transactions"}
    </button>
  )}
  {/* ✅ Add Blank Transaction button removed - users must add from asset list */}
</div>
    </div>
  );
};

export default Form;